import equal from 'deep-equal';

/**
 * Used to create configuration files.
 */
class ConfigurationFileCreator {
  constructor(appName, checksumFn, checksumSeed) {
    this.appName = void 0;
    this.checksumFn = void 0;
    this.checksumSeed = void 0;
    this.appName = appName;
    this.checksumFn = checksumFn;
    this.checksumSeed = checksumSeed;
  }
  /**
   * Creates the configuration file content for the given JSON configuration.
   *
   * @param jsonConfiguration The JSON configuration as JSON object or array.
   * @returns The configuration file content or an error object.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createConfigurationFileContent(jsonConfiguration) {
    let fileContent;
    if (this.checksumFn) {
      fileContent = {
        app: this.appName,
        model: jsonConfiguration,
        checksum: this.checksumFn(JSON.stringify(jsonConfiguration), this.checksumSeed)
      };
    } else {
      fileContent = {
        app: this.appName,
        model: jsonConfiguration
      };
    }
    return fileContent;
  }
  /**
   * Extracts the model string from the given file string and calls the checksum function on the extracted string.
   *
   * @param fileString The ConfigurationFileContent as string.
   */
  getChecksumFromFileContentString(fileString) {
    if (this.checksumFn) {
      let modelString = fileString.replace(new RegExp(`^\\s*{\\s*"app"\\s*:\\s*"${this.appName}"\\s*,\\s*"model"\\s*:\\s*`), '');
      modelString = modelString.replace(new RegExp(`\\s*,\\s*"checksum"\\s*:\\s*"?(-|[0-9]|[a-z]|[A-Z])*"?\\s*}\\s*$`), '');
      return this.checksumFn(modelString, this.checksumSeed);
    }
    return undefined;
  }
}

/**
 * The DataConsistency class ensures the consistency of data and provides
 * methods to migrate a
 * serialized model to the latest version
 */
class DataConsistency {
  /**
   * Constructor
   */
  constructor(logger, serializedObject, latestVersion) {
    this.logger = void 0;
    this.model = void 0;
    this.latestVersion = void 0;
    this.logger = logger;
    try {
      this.model = JSON.parse(serializedObject);
    } catch (_unused) {
      logger.error('Model.deserialize: Cannot parse serialized Object as JSON: ' + serializedObject);
    }
    this.latestVersion = latestVersion;
  }
  /**
   * Checks if the given model is the latest version
   */
  isLatestVersion() {
    if (Object.prototype.hasOwnProperty.call(this.model, '_moduleVersion')) {
      if (this.model._moduleVersion === this.latestVersion) {
        this.logger.log('model is latest version');
        return true;
      }
    }
    this.logger.log('model is not the latest version');
    return false;
  }
  /**
   * Migrates the model to the latest version and returns the updated object
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  migrate(dataMigration) {
    const migrationMethods = this.identifyMigrationMethods(dataMigration.migrationSteps);
    // check if identified methods exist
    for (const method of migrationMethods) {
      if (typeof dataMigration[method] !== 'function') {
        throw new Error('Migration method "' + method + '()" does not exist!');
      }
    }
    const model = dataMigration.run(this.model, migrationMethods);
    model._moduleVersion = this.latestVersion;
    return model;
  }
  /**
   * Compares current version with migrationSteps to identify migration-methods
   * which have to be executed
   */
  identifyMigrationMethods(migrationSteps) {
    const migrationMethods = [];
    if (!Object.prototype.hasOwnProperty.call(this.model, '_moduleVersion')) {
      this.logger.log('Model has no _moduleVersion property. Run all migration steps.');
      for (const version of migrationSteps) {
        migrationMethods.push(this.convertMigrationStepIntoMethodName(version));
      }
    } else {
      const currentVersion = this.model._moduleVersion;
      this.logger.log('Identified model version ' + currentVersion);
      for (const version of migrationSteps) {
        if (this.greaterThan(version, currentVersion)) {
          migrationMethods.push(this.convertMigrationStepIntoMethodName(version));
        }
      }
    }
    migrationMethods.reverse();
    this.logger.log('Run following migration methods:', migrationMethods);
    return migrationMethods;
  }
  /**
   * Converts version-number of migrationStep into standardized methodName
   */
  convertMigrationStepIntoMethodName(migrationStep) {
    return 'migrateTo_' + migrationStep.replace(/\./g, '_');
  }
  /**
   * Checks if provided version is greather than another.
   *
   * @param versionA The semver string
   * @param versionB The semver string
   */
  greaterThan(versionA, versionB) {
    return versionA.localeCompare(versionB, undefined, {
      numeric: true
    }) > 0;
  }
}

/**
 * DefaultDataMigration runs migration methods on a model object in order to
 * transform the model to
 * the latest version
 *
 * `migrationSteps` have to be defined in semver notation
 * `migrationMethods` have to be defined with following name schema:
 *      migrateTo_<majorVersion>_<minorVersion>_<patchVersion>...
 */
class DefaultDataMigration {
  constructor(logger) {
    this.migrationSteps = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.model = void 0;
    this.logger = void 0;
    this.logger = logger;
  }
  /**
   * Migrate model object to latest version by calling migration methods
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  run(model, migrationMethods) {
    this.model = model;
    for (const method of migrationMethods) {
      this[method]();
    }
    return this.model;
  }
}

const RETURN_TYPE = {
  /* eslint-disable @typescript-eslint/naming-convention */
  0: 'ok',
  1: 'warning',
  2: 'error'
  /* eslint-enable @typescript-eslint/naming-convention */
};
const RETURN_CODE = {
  /* eslint-disable @typescript-eslint/naming-convention */
  1: 'model validation',
  2: 'data validation'
  /* eslint-enable @typescript-eslint/naming-convention */
};
class ValidationResult {
  constructor(t, c, m = '') {
    this.t = void 0;
    this.c = void 0;
    this.m = void 0;
    this.type = void 0;
    this.code = void 0;
    this.typeDetail = void 0;
    this.codeDetail = void 0;
    this.message = '';
    this.t = t;
    this.c = c;
    this.m = m;
    if (!RETURN_TYPE[t]) {
      throw new Error(`RETURN_TYPE ${t} not defined`);
    }
    if (!RETURN_CODE[c]) {
      throw new Error(`RETURN_CODE ${c} not defined`);
    }
    this.type = t;
    this.code = c;
    this.typeDetail = RETURN_TYPE[t];
    this.codeDetail = RETURN_CODE[c];
    this.message = m;
  }
}

const lPad = (s, c, n) => {
  let repeat = n - s.length;
  repeat = repeat >= 0 ? repeat : 0;
  return c.repeat(repeat) + s;
};
const stringEqualsNumber = ev => {
  return parseFloat(ev[0]) === ev[1];
};
const point1DToString = p => {
  return '[' + lPad(p[0].toString(), ' ', 5) + ', ' + lPad(p[1].toString(), ' ', 5) + ']';
};
const point2DToString = p => {
  return '[' + lPad(p[0].toString(), ' ', 5) + ', ' + lPad(p[1].toString(), ' ', 5) + ', ' + lPad(p[2].toString(), ' ', 5) + ']';
};
const point3DToString = p => {
  return '[' + lPad(p[0].toString(), ' ', 5) + ', ' + lPad(p[1].toString(), ' ', 5) + ', ' + lPad(p[2].toString(), ' ', 5) + ', ' + lPad(p[3].toString(), ' ', 5) + ']';
};
const point1DListToString = (pl, prefix, delim = '') => {
  return pl.map(p => prefix + point1DToString(p)).join(delim);
};
const point2DListToString = (pl, prefix, delim = '') => {
  return pl.map(p => prefix + point2DToString(p)).join(delim);
};
const point3DListToString = (pl, prefix, delim = '') => {
  return pl.map(p => prefix + point3DToString(p)).join(delim);
};

class ExplanationJournal {
  constructor(leftWidth) {
    this.content = '';
    this.leftWidth = 30;
    this.subIndent = 4;
    this.addLinebreak = (n = 1) => {
      this.content += '\n'.repeat(n);
    };
    this.addTitle = s => {
      if (!s) {
        return;
      }
      this.content += '\n' + s + '\n';
      this.content += '='.repeat(s.length) + '\n\n';
    };
    this.addHeadline = s => {
      if (!s) {
        return;
      }
      this.content += '\n' + s + '\n';
      this.content += '-'.repeat(s.length) + '\n\n';
    };
    this.addText = (s, indent, prefix = '') => {
      if (!s) {
        return;
      }
      if (indent) {
        this.content += s.split('\n').map(e => prefix + ' '.repeat(indent - prefix.length) + e).join('\n') + '\n';
      } else {
        this.content += s + '\n';
      }
    };
    this.addSubText = s => this.addText(s, this.subIndent, '|');
    /***
     * creates a visual representation of a fraction formula
     *
     * @param result
     * @param numerator
     * @param denominator
     */
    this.addVisualFraction = (result, numerator, denominator) => {
      let res = '';
      if (denominator.length === 0) {
        this.addVisualProduct(result, numerator);
        return;
      }
      const numVarStr = numerator.map(e => stringEqualsNumber(e) ? e[0] + ' ' + e[2] : e[0]).join(' * ');
      const numValStr = numerator.map(e => `${e[1]} ${e[2]}`).join(' * ');
      const numVal = numerator.map(e => e[1]).reduce((p, c) => p * c);
      const denVarStr = denominator.map(e => stringEqualsNumber(e) ? e[0] + ' ' + e[2] : e[0]).join(' * ');
      const denValStr = denominator.map(e => `${e[1]} ${e[2]}`).join(' * ');
      const denVal = denominator.map(e => e[1]).reduce((p, c) => p * c);
      const lenghtVar = Math.max(numVarStr.length, denVarStr.length);
      const lenghtVal = Math.max(numValStr.length, denValStr.length);
      res += lPad('', ' ', this.leftWidth) + '   ' + numVarStr + '\n';
      res += lPad(result[0], ' ', this.leftWidth) + ' = ' + '―'.repeat(lenghtVar) + '\n';
      res += lPad('', ' ', this.leftWidth) + '   ' + denVarStr + '\n\n';
      res += lPad('', ' ', this.leftWidth) + '   ' + numValStr + '\n';
      res += lPad('', ' ', this.leftWidth) + ' = ' + '―'.repeat(lenghtVal) + '\n';
      res += lPad('', ' ', this.leftWidth) + '   ' + denValStr + '\n\n';
      let r = numVal / denVal;
      // call the rounding/truncation function if set
      if (result[3]) {
        r = result[3](r);
      }
      res += `${lPad('', ' ', this.leftWidth)} = ${r} ${result[2]}\n\n`;
      this.content += res + '\n';
    };
    /**
     *  creates a visual representation of a product formula
     */
    this.addVisualProduct = (result, product) => {
      let res = '';
      const numVarStr = product.map(e => stringEqualsNumber(e) ? e[0] + ' ' + e[2] : e[0]).join(' * ');
      const numValStr = product.map(e => `${e[1]} ${e[2]}`).join(' * ');
      const numVal = product.map(e => e[1]).reduce((p, c) => p * c);
      res += lPad(result[0], ' ', this.leftWidth) + ' = ' + numVarStr + '\n\n';
      res += lPad('', ' ', this.leftWidth) + ' = ' + numValStr + '\n\n';
      let r = numVal;
      // call the rounding/truncation function if set
      if (result[3]) {
        r = result[3](r);
      }
      res += `${lPad('', ' ', this.leftWidth)} = ${r} ${result[2]}\n\n`;
      this.content += res + '\n';
    };
    /**
     *  creates a visual representation of a sum formula
     */
    this.addVisualSum = (result, sum) => {
      let res = '';
      const numVarStr = sum.map(e => stringEqualsNumber(e) ? e[0] + ' ' + e[2] : e[0]).join(' + ');
      const numValStr = sum.map(e => `${e[1]} ${e[2]}`).join(' + ');
      const numVal = sum.map(e => e[1]).reduce((p, c) => p + c);
      res += lPad(result[0], ' ', this.leftWidth) + ' = ' + numVarStr + '\n\n';
      res += lPad('', ' ', this.leftWidth) + ' = ' + numValStr + '\n\n';
      let r = numVal;
      // call the rounding/truncation function if set
      if (result[3]) {
        r = result[3](r);
      }
      res += `${lPad('', ' ', this.leftWidth)} = ${r} ${result[2]}\n\n`;
      this.content += res + '\n';
    };
    this.addKeyValue = (key, value, unit = '') => {
      const v = typeof value === 'undefined' ? '' : value;
      this.content += `${lPad(key, ' ', this.leftWidth)} = ${v}${unit ? ' ' + unit : ''}\n`;
    };
    this.addConversionKeyValue = (key, valueFrom, unitFrom = '', valueTo, unitTo = '') => {
      const vFrom = typeof valueFrom === 'undefined' ? '' : valueFrom;
      const vTo = typeof valueTo === 'undefined' ? '' : valueTo;
      const padFrom = `${vFrom}${unitFrom ? ' ' + unitFrom : ''}`;
      this.content += `${lPad(key, ' ', this.leftWidth)} = ${lPad(padFrom, ' ', this.leftWidth)} => ${vTo}${unitTo ? ' ' + unitTo : ''}\n`;
    };
    this.addCheckValue = (key, value, unit, isValid, reason) => {
      let valueAsString = '';
      if (!(typeof value === 'undefined')) {
        valueAsString = value.toString(10);
      }
      this.content += lPad(key, ' ', this.leftWidth) + ' = ' + lPad(valueAsString, ' ', 10) + lPad(unit, ' ', 10) + (isValid ? '  ✅' : '  ❌') + reason + '\n';
    };
    this.addPoint1D = (s, point) => {
      this.content += lPad(s, ' ', this.leftWidth) + ' = ' + point1DToString(point) + '\n';
    };
    this.addPoint2D = (s, point) => {
      this.content += lPad(s, ' ', this.leftWidth) + ' = ' + point2DToString(point) + '\n';
    };
    this.addPoint3D = (s, point) => {
      this.content += lPad(s, ' ', this.leftWidth) + ' = ' + point3DToString(point) + '\n';
    };
    this.addPoint1DList = (s, points, uLabel = '', yLabel = '') => {
      this.content += lPad(s, ' ', this.leftWidth) + ' = [   [' + uLabel + ']  [' + yLabel + ']\n';
      if (points.length) {
        this.content += point1DListToString(points, ' '.repeat(this.leftWidth + 5), '\n') + '\n';
      }
      this.content += lPad('', ' ', this.leftWidth) + '   ]\n\n';
    };
    this.addPoint2DList = (s, points) => {
      this.content += lPad(s, ' ', this.leftWidth) + ' = [   [MPa]  [m/s]   [nm/m]\n';
      if (points.length) {
        this.content += point2DListToString(points, ' '.repeat(this.leftWidth + 5), '\n') + '\n';
      }
      this.content += lPad('', ' ', this.leftWidth) + '   ]\n\n';
    };
    this.addPoint3DList = (s, points) => {
      this.content += lPad(s, ' ', this.leftWidth) + ' = [   [MPa]  [m/s]   [°C]  [nm/m]\n';
      if (points.length) {
        this.content += point3DListToString(points, ' '.repeat(this.leftWidth + 5), '\n') + '\n';
      }
      this.content += lPad('', ' ', this.leftWidth) + '   ]\n\n';
    };
    this.getJournal = () => {
      return this.content;
    };
    if (leftWidth) {
      this.leftWidth = leftWidth;
    }
  }
}

/**
 * The header used for feature flags handover via interceptors.
 */
const featureFlagHeader = 'kopla-feature-flags';
/*
 * The separator of the feature flags in the header.
 */
const featureFlagHeaderSeparator = ',';
/**
 * The separator of the feature flag value in the header.
 */
const featureFlagHeaderValueSeparator = '=';

var AvailableLocalizedPdfServiceFonts;
(function (AvailableLocalizedPdfServiceFonts) {
  AvailableLocalizedPdfServiceFonts["default"] = "NotoSans";
  AvailableLocalizedPdfServiceFonts["traditionalChinese"] = "NotoSansTC";
  AvailableLocalizedPdfServiceFonts["simplifiedChinese"] = "NotoSansSC";
  AvailableLocalizedPdfServiceFonts["korean"] = "NotoSansKR";
  AvailableLocalizedPdfServiceFonts["japanese"] = "NotoSansJP";
  /**
   * Special font for math symbols
   */
  AvailableLocalizedPdfServiceFonts["math"] = "NotoSansMath";
})(AvailableLocalizedPdfServiceFonts || (AvailableLocalizedPdfServiceFonts = {}));

/**
 * Enumeration {@link SalesforceCountryCode} enumerates countries accepted by igus' Marketing Cloud Integration.
 * Default SalesForce list is specified in https://help.salesforce.com/s/articleView?id=sf.admin_state_country_picklists_standard_countries.htm ,
 * However, there are modifications to the official salesforce list, because they are requested by igus' CRM team.
 * The use of this MCI list has been approved by CRM team and we can therefore assume that it represents igus' official communication with the public.
 *
 * Be aware that although the list largely resembles ISO3166-1-Alpha-2 country codes as per https://www.iso.org/obp/ui/#search ,
 * but we cannot add countries without approval from the CRM team.
 */
/* eslint-disable @typescript-eslint/naming-convention */
var SalesforceCountryCode;
(function (SalesforceCountryCode) {
  SalesforceCountryCode["AD"] = "AD";
  SalesforceCountryCode["AE"] = "AE";
  SalesforceCountryCode["AF"] = "AF";
  SalesforceCountryCode["AG"] = "AG";
  SalesforceCountryCode["AI"] = "AI";
  SalesforceCountryCode["AL"] = "AL";
  SalesforceCountryCode["AM"] = "AM";
  SalesforceCountryCode["AO"] = "AO";
  SalesforceCountryCode["AQ"] = "AQ";
  SalesforceCountryCode["AR"] = "AR";
  SalesforceCountryCode["AT"] = "AT";
  SalesforceCountryCode["AU"] = "AU";
  SalesforceCountryCode["AW"] = "AW";
  SalesforceCountryCode["AX"] = "AX";
  SalesforceCountryCode["AZ"] = "AZ";
  SalesforceCountryCode["BA"] = "BA";
  SalesforceCountryCode["BB"] = "BB";
  SalesforceCountryCode["BD"] = "BD";
  SalesforceCountryCode["BE"] = "BE";
  SalesforceCountryCode["BF"] = "BF";
  SalesforceCountryCode["BG"] = "BG";
  SalesforceCountryCode["BH"] = "BH";
  SalesforceCountryCode["BI"] = "BI";
  SalesforceCountryCode["BJ"] = "BJ";
  SalesforceCountryCode["BL"] = "BL";
  SalesforceCountryCode["BM"] = "BM";
  SalesforceCountryCode["BN"] = "BN";
  SalesforceCountryCode["BO"] = "BO";
  SalesforceCountryCode["BQ"] = "BQ";
  SalesforceCountryCode["BR"] = "BR";
  SalesforceCountryCode["BS"] = "BS";
  SalesforceCountryCode["BT"] = "BT";
  SalesforceCountryCode["BV"] = "BV";
  SalesforceCountryCode["BW"] = "BW";
  SalesforceCountryCode["BY"] = "BY";
  SalesforceCountryCode["BZ"] = "BZ";
  SalesforceCountryCode["CA"] = "CA";
  SalesforceCountryCode["CC"] = "CC";
  SalesforceCountryCode["CD"] = "CD";
  SalesforceCountryCode["CF"] = "CF";
  SalesforceCountryCode["CG"] = "CG";
  SalesforceCountryCode["CH"] = "CH";
  SalesforceCountryCode["CI"] = "CI";
  SalesforceCountryCode["CK"] = "CK";
  SalesforceCountryCode["CL"] = "CL";
  SalesforceCountryCode["CM"] = "CM";
  SalesforceCountryCode["CN"] = "CN";
  SalesforceCountryCode["CO"] = "CO";
  SalesforceCountryCode["CR"] = "CR";
  SalesforceCountryCode["CU"] = "CU";
  SalesforceCountryCode["CV"] = "CV";
  SalesforceCountryCode["CW"] = "CW";
  SalesforceCountryCode["CX"] = "CX";
  SalesforceCountryCode["CY"] = "CY";
  SalesforceCountryCode["CZ"] = "CZ";
  SalesforceCountryCode["DE"] = "DE";
  SalesforceCountryCode["DJ"] = "DJ";
  SalesforceCountryCode["DK"] = "DK";
  SalesforceCountryCode["DM"] = "DM";
  SalesforceCountryCode["DO"] = "DO";
  SalesforceCountryCode["DZ"] = "DZ";
  SalesforceCountryCode["EC"] = "EC";
  SalesforceCountryCode["EE"] = "EE";
  SalesforceCountryCode["EG"] = "EG";
  SalesforceCountryCode["EH"] = "EH";
  SalesforceCountryCode["ER"] = "ER";
  SalesforceCountryCode["ES"] = "ES";
  SalesforceCountryCode["ET"] = "ET";
  SalesforceCountryCode["FI"] = "FI";
  SalesforceCountryCode["FJ"] = "FJ";
  SalesforceCountryCode["FK"] = "FK";
  SalesforceCountryCode["FO"] = "FO";
  SalesforceCountryCode["FR"] = "FR";
  SalesforceCountryCode["GA"] = "GA";
  SalesforceCountryCode["GB"] = "GB";
  SalesforceCountryCode["GD"] = "GD";
  SalesforceCountryCode["GE"] = "GE";
  SalesforceCountryCode["GF"] = "GF";
  SalesforceCountryCode["GG"] = "GG";
  SalesforceCountryCode["GH"] = "GH";
  SalesforceCountryCode["GI"] = "GI";
  SalesforceCountryCode["GL"] = "GL";
  SalesforceCountryCode["GM"] = "GM";
  SalesforceCountryCode["GN"] = "GN";
  SalesforceCountryCode["GP"] = "GP";
  SalesforceCountryCode["GQ"] = "GQ";
  SalesforceCountryCode["GR"] = "GR";
  SalesforceCountryCode["GS"] = "GS";
  SalesforceCountryCode["GT"] = "GT";
  SalesforceCountryCode["GW"] = "GW";
  SalesforceCountryCode["GY"] = "GY";
  SalesforceCountryCode["HM"] = "HM";
  SalesforceCountryCode["HN"] = "HN";
  SalesforceCountryCode["HR"] = "HR";
  SalesforceCountryCode["HT"] = "HT";
  SalesforceCountryCode["HU"] = "HU";
  SalesforceCountryCode["ID"] = "ID";
  SalesforceCountryCode["IE"] = "IE";
  SalesforceCountryCode["IL"] = "IL";
  SalesforceCountryCode["IM"] = "IM";
  SalesforceCountryCode["IN"] = "IN";
  SalesforceCountryCode["IO"] = "IO";
  SalesforceCountryCode["IQ"] = "IQ";
  SalesforceCountryCode["IR"] = "IR";
  SalesforceCountryCode["IS"] = "IS";
  SalesforceCountryCode["IT"] = "IT";
  SalesforceCountryCode["JE"] = "JE";
  SalesforceCountryCode["JM"] = "JM";
  SalesforceCountryCode["JO"] = "JO";
  SalesforceCountryCode["JP"] = "JP";
  SalesforceCountryCode["KE"] = "KE";
  SalesforceCountryCode["KG"] = "KG";
  SalesforceCountryCode["KH"] = "KH";
  SalesforceCountryCode["KI"] = "KI";
  SalesforceCountryCode["KM"] = "KM";
  SalesforceCountryCode["KN"] = "KN";
  SalesforceCountryCode["KP"] = "KP";
  SalesforceCountryCode["KR"] = "KR";
  SalesforceCountryCode["KW"] = "KW";
  SalesforceCountryCode["KY"] = "KY";
  SalesforceCountryCode["KZ"] = "KZ";
  SalesforceCountryCode["LA"] = "LA";
  SalesforceCountryCode["LB"] = "LB";
  SalesforceCountryCode["LC"] = "LC";
  SalesforceCountryCode["LI"] = "LI";
  SalesforceCountryCode["LK"] = "LK";
  SalesforceCountryCode["LR"] = "LR";
  SalesforceCountryCode["LS"] = "LS";
  SalesforceCountryCode["LT"] = "LT";
  SalesforceCountryCode["LU"] = "LU";
  SalesforceCountryCode["LV"] = "LV";
  SalesforceCountryCode["LY"] = "LY";
  SalesforceCountryCode["MA"] = "MA";
  SalesforceCountryCode["MC"] = "MC";
  SalesforceCountryCode["MD"] = "MD";
  SalesforceCountryCode["ME"] = "ME";
  SalesforceCountryCode["MF"] = "MF";
  SalesforceCountryCode["MG"] = "MG";
  SalesforceCountryCode["MK"] = "MK";
  SalesforceCountryCode["ML"] = "ML";
  SalesforceCountryCode["MM"] = "MM";
  SalesforceCountryCode["MN"] = "MN";
  SalesforceCountryCode["MO"] = "MO";
  SalesforceCountryCode["MQ"] = "MQ";
  SalesforceCountryCode["MR"] = "MR";
  SalesforceCountryCode["MS"] = "MS";
  SalesforceCountryCode["MT"] = "MT";
  SalesforceCountryCode["MU"] = "MU";
  SalesforceCountryCode["MV"] = "MV";
  SalesforceCountryCode["MW"] = "MW";
  SalesforceCountryCode["MX"] = "MX";
  SalesforceCountryCode["MY"] = "MY";
  SalesforceCountryCode["MZ"] = "MZ";
  SalesforceCountryCode["NA"] = "NA";
  SalesforceCountryCode["NC"] = "NC";
  SalesforceCountryCode["NE"] = "NE";
  SalesforceCountryCode["NF"] = "NF";
  SalesforceCountryCode["NG"] = "NG";
  SalesforceCountryCode["NI"] = "NI";
  SalesforceCountryCode["NL"] = "NL";
  SalesforceCountryCode["NO"] = "NO";
  SalesforceCountryCode["NP"] = "NP";
  SalesforceCountryCode["NR"] = "NR";
  SalesforceCountryCode["NU"] = "NU";
  SalesforceCountryCode["NZ"] = "NZ";
  SalesforceCountryCode["OM"] = "OM";
  SalesforceCountryCode["PA"] = "PA";
  SalesforceCountryCode["PE"] = "PE";
  SalesforceCountryCode["PF"] = "PF";
  SalesforceCountryCode["PG"] = "PG";
  SalesforceCountryCode["PH"] = "PH";
  SalesforceCountryCode["PK"] = "PK";
  SalesforceCountryCode["PL"] = "PL";
  SalesforceCountryCode["PM"] = "PM";
  SalesforceCountryCode["PN"] = "PN";
  SalesforceCountryCode["PS"] = "PS";
  SalesforceCountryCode["PT"] = "PT";
  SalesforceCountryCode["PY"] = "PY";
  SalesforceCountryCode["QA"] = "QA";
  SalesforceCountryCode["RE"] = "RE";
  SalesforceCountryCode["RO"] = "RO";
  SalesforceCountryCode["RS"] = "RS";
  SalesforceCountryCode["RU"] = "RU";
  SalesforceCountryCode["RW"] = "RW";
  SalesforceCountryCode["SA"] = "SA";
  SalesforceCountryCode["SB"] = "SB";
  SalesforceCountryCode["SC"] = "SC";
  SalesforceCountryCode["SD"] = "SD";
  SalesforceCountryCode["SE"] = "SE";
  SalesforceCountryCode["SG"] = "SG";
  SalesforceCountryCode["SH"] = "SH";
  SalesforceCountryCode["SI"] = "SI";
  SalesforceCountryCode["SJ"] = "SJ";
  SalesforceCountryCode["SK"] = "SK";
  SalesforceCountryCode["SL"] = "SL";
  SalesforceCountryCode["SM"] = "SM";
  SalesforceCountryCode["SN"] = "SN";
  SalesforceCountryCode["SO"] = "SO";
  SalesforceCountryCode["SR"] = "SR";
  SalesforceCountryCode["SS"] = "SS";
  SalesforceCountryCode["ST"] = "ST";
  SalesforceCountryCode["SV"] = "SV";
  SalesforceCountryCode["SX"] = "SX";
  SalesforceCountryCode["SY"] = "SY";
  SalesforceCountryCode["SZ"] = "SZ";
  SalesforceCountryCode["TC"] = "TC";
  SalesforceCountryCode["TD"] = "TD";
  SalesforceCountryCode["TF"] = "TF";
  SalesforceCountryCode["TG"] = "TG";
  SalesforceCountryCode["TH"] = "TH";
  SalesforceCountryCode["TJ"] = "TJ";
  SalesforceCountryCode["TK"] = "TK";
  SalesforceCountryCode["TL"] = "TL";
  SalesforceCountryCode["TM"] = "TM";
  SalesforceCountryCode["TN"] = "TN";
  SalesforceCountryCode["TO"] = "TO";
  SalesforceCountryCode["TR"] = "TR";
  SalesforceCountryCode["TT"] = "TT";
  SalesforceCountryCode["TV"] = "TV";
  SalesforceCountryCode["TW"] = "TW";
  SalesforceCountryCode["TZ"] = "TZ";
  SalesforceCountryCode["UA"] = "UA";
  SalesforceCountryCode["UG"] = "UG";
  SalesforceCountryCode["US"] = "US";
  SalesforceCountryCode["UY"] = "UY";
  SalesforceCountryCode["UZ"] = "UZ";
  SalesforceCountryCode["VA"] = "VA";
  SalesforceCountryCode["VC"] = "VC";
  SalesforceCountryCode["VE"] = "VE";
  SalesforceCountryCode["VG"] = "VG";
  SalesforceCountryCode["VN"] = "VN";
  SalesforceCountryCode["VU"] = "VU";
  SalesforceCountryCode["WF"] = "WF";
  SalesforceCountryCode["WS"] = "WS";
  SalesforceCountryCode["XK"] = "XK";
  SalesforceCountryCode["YE"] = "YE";
  SalesforceCountryCode["YT"] = "YT";
  SalesforceCountryCode["ZA"] = "ZA";
  SalesforceCountryCode["ZM"] = "ZM";
  SalesforceCountryCode["ZW"] = "ZW";
})(SalesforceCountryCode || (SalesforceCountryCode = {}));
/**
 * A list containing country codes as per
 * https://help.salesforce.com/s/articleView?id=sf.admin_state_country_picklists_standard_countries.htm
 *
 * See also {@link SalesforceCountryCode}.
 */
const SALESFORCE_COUNTRY_CODES = Object.values(SalesforceCountryCode);

const LOCALIZATION_KEY_ROOT = 'kopla.salesforce_countries.';
/**
 * Returns the localization key for a given {@link SalesforceCountryCode}.
 *
 * @param countryCode the {@link SalesforceCountryCode} to return the localization key for
 * @returns the localization key
 */
const getLocalizationKeyForSalesforceCountryCode = countryCode => {
  return LOCALIZATION_KEY_ROOT + countryCode;
};
/**
 * Return the complete list of {@link SalesforceCountry}s with translation keys pre-populated in
 * {@link SalesforceCountry.label}.
 *
 * @returns the complete list of {@link SalesforceCountry}s
 */
const getSalesforceCountries = () => {
  const countries = [];
  for (const countryCode of SALESFORCE_COUNTRY_CODES) {
    countries.push({
      code: countryCode,
      label: LOCALIZATION_KEY_ROOT + countryCode
    });
  }
  return countries;
};

const defaultIProCulture = {
  countryName: 'Germany',
  countryCode: 'DE',
  language: 'de'
};
const iProCultures = [{
  countryName: 'Argentina',
  countryCode: 'AR',
  language: 'es'
}, {
  countryName: 'Australia',
  countryCode: 'AU',
  language: 'en'
}, {
  countryName: 'Austria',
  countryCode: 'AT',
  language: 'de'
}, {
  countryName: 'Belarus',
  countryCode: 'BY',
  language: 'ru'
}, {
  countryName: 'Belgium',
  countryCode: 'BE',
  language: 'fr'
}, {
  countryName: 'Belgium',
  countryCode: 'BE',
  language: 'nl'
}, {
  countryName: 'Belgium',
  countryCode: 'BE',
  language: 'de'
}, {
  countryName: 'Brasil',
  countryCode: 'BR',
  language: 'pt'
}, {
  countryName: 'Bulgaria',
  countryCode: 'BG',
  language: 'bg'
}, {
  countryName: 'Bulgaria',
  countryCode: 'BG',
  language: 'en'
}, {
  countryName: 'Canada',
  countryCode: 'CA',
  language: 'en'
}, {
  countryName: 'Canada',
  countryCode: 'CA',
  language: 'fr'
}, {
  countryName: 'Chile',
  countryCode: 'CL',
  language: 'es'
}, {
  countryName: 'China',
  countryCode: 'CN',
  language: 'zh'
}, {
  countryName: 'Croatia',
  countryCode: 'HR',
  language: 'en'
}, {
  countryName: 'Czech Republic',
  countryCode: 'CZ',
  language: 'cs'
}, {
  countryName: 'Denmark',
  countryCode: 'DK',
  language: 'da'
}, {
  countryName: 'Egypt',
  countryCode: 'EG',
  language: 'en'
}, {
  countryName: 'Estonia',
  countryCode: 'EE',
  language: 'en'
}, {
  countryName: 'Finland',
  countryCode: 'FI',
  language: 'en'
}, {
  countryName: 'France',
  countryCode: 'FR',
  language: 'fr'
}, defaultIProCulture, {
  countryName: 'Germany',
  countryCode: 'DE',
  language: 'en'
}, {
  countryName: 'Germany',
  countryCode: 'DE',
  language: 'es'
}, {
  countryName: 'Germany',
  countryCode: 'DE',
  language: 'fr'
}, {
  countryName: 'Greece',
  countryCode: 'GR',
  language: 'en'
}, {
  countryName: 'Hungary',
  countryCode: 'HU',
  language: 'hu'
}, {
  countryName: 'India',
  countryCode: 'IN',
  language: 'en'
}, {
  countryName: 'Indonesia',
  countryCode: 'ID',
  language: 'id'
}, {
  countryName: 'Ireland',
  countryCode: 'IE',
  language: 'en'
}, {
  countryName: 'Israel',
  countryCode: 'IL',
  language: 'en'
}, {
  countryName: 'Italy',
  countryCode: 'IT',
  language: 'it'
}, {
  countryName: 'Japan',
  countryCode: 'JP',
  language: 'ja'
}, {
  countryName: 'Korea',
  countryCode: 'KR',
  language: 'ko'
}, {
  countryName: 'Lebanon',
  countryCode: 'LB',
  language: 'en'
}, {
  countryName: 'Lithuania',
  countryCode: 'LT',
  language: 'en'
}, {
  countryName: 'Malaysia',
  countryCode: 'MY',
  language: 'en'
}, {
  countryName: 'Mexico',
  countryCode: 'MX',
  language: 'es'
}, {
  countryName: 'Netherlands',
  countryCode: 'NL',
  language: 'nl'
}, {
  countryName: 'New Zealand',
  countryCode: 'NZ',
  language: 'en'
}, {
  countryName: 'Nigeria',
  countryCode: 'NG',
  language: 'en'
}, {
  countryName: 'Norway',
  countryCode: 'NO',
  language: 'en'
}, {
  countryName: 'Norway',
  countryCode: 'NO',
  language: 'no'
}, {
  countryName: 'Poland',
  countryCode: 'PL',
  language: 'pl'
}, {
  countryName: 'Portugal',
  countryCode: 'PT',
  language: 'pt'
}, {
  countryName: 'Romania',
  countryCode: 'RO',
  language: 'ro'
}, {
  countryName: 'Russia',
  countryCode: 'RU',
  language: 'ru'
}, {
  countryName: 'Serbia',
  countryCode: 'RS',
  language: 'en'
}, {
  countryName: 'Singapore',
  countryCode: 'SG',
  language: 'en'
}, {
  countryName: 'Slovakia',
  countryCode: 'SK',
  language: 'cs'
}, {
  countryName: 'Slovenia',
  countryCode: 'SI',
  language: 'en'
}, {
  countryName: 'South Africa',
  countryCode: 'ZA',
  language: 'en'
}, {
  countryName: 'Spain',
  countryCode: 'ES',
  language: 'es'
}, {
  countryName: 'Sweden',
  countryCode: 'SE',
  language: 'sv'
}, {
  countryName: 'Switzerland',
  countryCode: 'CH',
  language: 'de'
}, {
  countryName: 'Switzerland',
  countryCode: 'CH',
  language: 'it'
}, {
  countryName: 'Switzerland',
  countryCode: 'CH',
  language: 'fr'
}, {
  countryName: 'Taiwan',
  countryCode: 'TW',
  language: 'zh'
}, {
  countryName: 'Thailand',
  countryCode: 'TH',
  language: 'th'
}, {
  countryName: 'Tunisia',
  countryCode: 'TN',
  language: 'fr'
}, {
  countryName: 'Turkey',
  countryCode: 'TR',
  language: 'tr'
}, {
  countryName: 'Ukraine',
  countryCode: 'UA',
  language: 'ru'
}, {
  countryName: 'United Kingdom',
  countryCode: 'GB',
  language: 'en'
}, {
  countryName: 'United States',
  countryCode: 'US',
  language: 'en'
}, {
  countryName: 'Vietnam',
  countryCode: 'VN',
  language: 'vi'
}];

const iProDomains = [{
  country: 'AR',
  language: 'es',
  url: 'www.igus.com.ar'
}, {
  country: 'AT',
  language: 'de',
  url: 'www.igus.at'
}, {
  country: 'AU',
  language: 'en',
  url: 'www.igus.com.au'
}, {
  country: 'BE',
  language: 'fr',
  url: 'www.igus.be'
}, {
  country: 'BE',
  language: 'nl',
  url: 'www.igus.be'
}, {
  country: 'BE',
  language: 'de',
  url: 'www.igus.be'
}, {
  country: 'BG',
  language: 'en',
  url: 'www.igus.bg'
}, {
  country: 'BR',
  language: 'pt',
  url: 'www.igus.com.br'
}, {
  country: 'BY',
  language: 'ru',
  url: 'www.igus.by'
}, {
  country: 'CA',
  language: 'fr',
  url: 'www.igus.ca'
}, {
  country: 'CA',
  language: 'en',
  url: 'www.igus.ca'
}, {
  country: 'CH',
  language: 'it',
  url: 'www.igus.ch'
}, {
  country: 'CH',
  language: 'fr',
  url: 'www.igus.ch'
}, {
  country: 'CH',
  language: 'de',
  url: 'www.igus.ch'
}, {
  country: 'CL',
  language: 'es',
  url: 'www.igus.cl'
}, {
  country: 'CN',
  language: 'zh',
  url: 'www.igus.cn'
}, {
  country: 'CZ',
  language: 'cs',
  url: 'www.igus.cz'
}, {
  country: 'DE',
  language: 'de',
  url: 'www.igus.de'
}, {
  country: 'DE',
  language: 'en',
  url: 'www.igus.eu'
}, {
  country: 'DE',
  language: 'fr',
  url: 'www.igus.eu'
}, {
  country: 'DK',
  language: 'da',
  url: 'www.igus.dk'
}, {
  country: 'EE',
  language: 'en',
  url: 'www.igus.ee'
}, {
  country: 'EG',
  language: 'en',
  url: 'www.igus.com.eg'
}, {
  country: 'ES',
  language: 'es',
  url: 'www.igus.es'
}, {
  country: 'FI',
  language: 'en',
  url: 'www.igus.fi'
}, {
  country: 'FR',
  language: 'fr',
  url: 'www.igus.fr'
}, {
  country: 'GB',
  language: 'en',
  url: 'www.igus.co.uk'
}, {
  country: 'GR',
  language: 'en',
  url: 'www.igus.gr'
}, {
  country: 'HR',
  language: 'en',
  url: 'www.igus.hr'
}, {
  country: 'HU',
  language: 'hu',
  url: 'www.igus.hu'
}, {
  country: 'ID',
  language: 'id',
  url: 'www.igus.co.id'
}, {
  country: 'IE',
  language: 'en',
  url: 'www.igus.ie'
}, {
  country: 'IL',
  language: 'en',
  url: 'www.igus.co.il'
}, {
  country: 'IN',
  language: 'en',
  url: 'www.igus.in'
}, {
  country: 'IT',
  language: 'it',
  url: 'www.igus.it'
}, {
  country: 'JP',
  language: 'ja',
  url: 'www.igus.co.jp'
}, {
  country: 'KR',
  language: 'ko',
  url: 'www.igus.kr'
}, {
  country: 'LB',
  language: 'en',
  url: 'www.igus.eu'
}, {
  country: 'LT',
  language: 'en',
  url: 'www.igus.lt'
}, {
  country: 'MX',
  language: 'es',
  url: 'www.igus.com.mx'
}, {
  country: 'MY',
  language: 'en',
  url: 'www.igus.my'
}, {
  country: 'NG',
  language: 'en',
  url: 'www.igus.eu'
}, {
  country: 'NL',
  language: 'nl',
  url: 'www.igus.nl'
}, {
  country: 'NO',
  language: 'en',
  url: 'www.igus.no'
}, {
  country: 'NZ',
  language: 'en',
  url: 'www.igus.co.nz'
}, {
  country: 'PL',
  language: 'pl',
  url: 'www.igus.pl'
}, {
  country: 'PT',
  language: 'pt',
  url: 'www.igus.pt'
}, {
  country: 'RO',
  language: 'ro',
  url: 'www.igus.ro'
}, {
  country: 'RS',
  language: 'en',
  url: 'www.igus.rs'
}, {
  country: 'RU',
  language: 'ru',
  url: 'www.igus.ru'
}, {
  country: 'SE',
  language: 'sv',
  url: 'www.igusab.se'
}, {
  country: 'SG',
  language: 'en',
  url: 'www.igus.sg'
}, {
  country: 'SI',
  language: 'en',
  url: 'www.igus.si'
}, {
  country: 'SK',
  language: 'sk',
  url: 'www.igus.sk'
}, {
  country: 'TH',
  language: 'th',
  url: 'www.igus.co.th'
}, {
  country: 'TN',
  language: 'fr',
  url: 'www.igus.eu'
}, {
  country: 'TR',
  language: 'tr',
  url: 'www.igus.com.tr'
}, {
  country: 'TW',
  language: 'zh',
  url: 'www.igus.com.tw'
}, {
  country: 'UA',
  language: 'ru',
  url: 'www.igus.com.ua'
}, {
  country: 'US',
  language: 'en',
  url: 'www.igus.com'
}, {
  country: 'VN',
  language: 'vi',
  url: 'www.igus.vn'
}, {
  country: 'ZA',
  language: 'en',
  url: 'www.igus.co.za'
}, {
  url: 'www.igus.bo'
}, {
  url: 'www.igus.ph'
}, {
  url: 'www.igus.hk'
}, {
  url: 'www.igus.mobi'
}];

/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Uniquely identifies a requesting application towards the IproSvc AppService api.
 * This is necessary for the backend services to operate successfully.
 * Keep in sync with registered app ids at the MMAs IproSvc AppService api.
 */
var IproSvcAppServiceAppId;
(function (IproSvcAppServiceAppId) {
  IproSvcAppServiceAppId["CFP"] = "CFP";
  IproSvcAppServiceAppId["DDX"] = "DDX";
  IproSvcAppServiceAppId["DEK"] = "DEK";
  IproSvcAppServiceAppId["DGE"] = "DGE";
  IproSvcAppServiceAppId["DHC"] = "DHC";
  IproSvcAppServiceAppId["DLE"] = "DLE";
  IproSvcAppServiceAppId["DLK"] = "DLK";
  IproSvcAppServiceAppId["DLX"] = "DLX";
  IproSvcAppServiceAppId["DMX"] = "DMX";
  IproSvcAppServiceAppId["DRK"] = "DRK";
  IproSvcAppServiceAppId["DRL"] = "DRL";
  IproSvcAppServiceAppId["DRW"] = "DRW";
  IproSvcAppServiceAppId["DRX"] = "DRX";
  IproSvcAppServiceAppId["DSE"] = "DSE";
  IproSvcAppServiceAppId["DSK"] = "DSK";
  IproSvcAppServiceAppId["DTC"] = "DTC";
  IproSvcAppServiceAppId["E13"] = "E13";
  IproSvcAppServiceAppId["E60"] = "E60";
  IproSvcAppServiceAppId["ECF"] = "ECF";
  IproSvcAppServiceAppId["ECL"] = "ECL";
  IproSvcAppServiceAppId["ECM"] = "ECM";
  IproSvcAppServiceAppId["ECX"] = "ECX";
  IproSvcAppServiceAppId["EFP"] = "EFP";
  IproSvcAppServiceAppId["EIC"] = "EIC";
  IproSvcAppServiceAppId["EKE"] = "EKE";
  IproSvcAppServiceAppId["ERK"] = "ERK";
  IproSvcAppServiceAppId["ESL"] = "ESL";
  IproSvcAppServiceAppId["ESP"] = "ESP";
  IproSvcAppServiceAppId["RCP"] = "RCP";
  IproSvcAppServiceAppId["CSL"] = "CSL";
  IproSvcAppServiceAppId["ESC"] = "ESC";
  IproSvcAppServiceAppId["GCC"] = "GCC";
  IproSvcAppServiceAppId["IBD"] = "IBD";
  IproSvcAppServiceAppId["IBH"] = "IBH";
  IproSvcAppServiceAppId["IBP"] = "IBP";
  IproSvcAppServiceAppId["ICD"] = "ICD";
  IproSvcAppServiceAppId["ICN"] = "ICN";
  IproSvcAppServiceAppId["IDD"] = "IDD";
  IproSvcAppServiceAppId["IDD_P2M"] = "IDD_P2M";
  IproSvcAppServiceAppId["IHP"] = "IHP";
  IproSvcAppServiceAppId["IKP"] = "IKP";
  IproSvcAppServiceAppId["IMD"] = "IMD";
  IproSvcAppServiceAppId["ISP"] = "ISP";
  IproSvcAppServiceAppId["ITOOLS"] = "ITOOLS";
  IproSvcAppServiceAppId["KPL"] = "KPL";
  IproSvcAppServiceAppId["LTW"] = "LTW";
  IproSvcAppServiceAppId["RBA"] = "RBA";
  IproSvcAppServiceAppId["RLK"] = "RLK";
  IproSvcAppServiceAppId["RLK_SCHUNK"] = "RLK_SCHUNK";
  IproSvcAppServiceAppId["RMC"] = "RMC";
  IproSvcAppServiceAppId["SDR"] = "SDR";
  IproSvcAppServiceAppId["SLK"] = "SLK";
  IproSvcAppServiceAppId["XIL"] = "XIL";
  IproSvcAppServiceAppId["XRE"] = "XRE";
})(IproSvcAppServiceAppId || (IproSvcAppServiceAppId = {}));

/**
 * Implements the Parser Interface and prints LoggerItems to the console.
 */
class ConsoleParser {
  constructor() {
    this.groupLevel = 0;
    this.posInGroup = 0;
    this.emptyGroup = 0;
    this.items = [];
  }
  /**
   * Iterates over an Array of LoggerItems.
   */
  generateOutput(items) {
    this.items = items;
    for (const item of items) {
      this.posInGroup++;
      this.consoleOut(item);
    }
  }
  /**
   * Prints a LoggerItem to the console and handles group LoggerItems
   * intelligently.
   */
  consoleOut(item) {
    const logLevel = item.logLevel;
    const message = item.message;
    if (item.logLevel === 'group') {
      this.groupLevel++;
    }
    const separator = Array(this.groupLevel * 4).join(' ');
    // Only handle grouped items (group > 0)
    if (this.groupLevel > 0) {
      // TODO Check how to handle objects / other types
      message.unshift(separator);
      switch (logLevel) {
        case 'debug':
          // debug does not exists anymore
          console.log(...message);
          break;
        case 'log':
          console.log(...message);
          break;
        case 'info':
          console.log(...message);
          break;
        case 'warn':
          console.warn(...message);
          break;
        case 'error':
          console.error(...message);
          break;
        case 'group':
          if (!this.groupIsEmpty()) {
            console.log(...message);
          } else {
            this.emptyGroup++;
          }
          break;
        case 'groupEnd':
          if (this.emptyGroup === 0) {
            console.log(...message);
          } else {
            this.emptyGroup--;
          }
          break;
        default:
          throw new Error('LogLevel ' + logLevel + ' not supported');
      }
      if (item.logLevel === 'groupEnd') {
        this.groupLevel--;
      }
    }
  }
  /**
   * Checks if an opened group contains loggerItems or other groups.
   */
  groupIsEmpty() {
    let msgFound = 0;
    let groupsOpen = 0;
    for (let i = this.posInGroup; i < this.items.length; i++) {
      const item = this.items[i];
      if (item.logLevel === 'group') {
        groupsOpen++;
      } else if (item.logLevel === 'groupEnd') {
        if (groupsOpen === 0) {
          break;
        }
        groupsOpen--;
      } else {
        msgFound = 1;
        break;
      }
    }
    if (msgFound === 1) {
      return false;
    }
    return true;
  }
}

const LOGLEVEL_SILENT = 0;
const LOGLEVEL_ERROR = 1;
const LOGLEVEL_WARN = 2;
const LOGLEVEL_INFO = 3;
const LOGLEVEL_LOG = 4;
// const LOGLEVEL_DEBUG = 4;
/**
 * The DefaultLogger class stores log messages for later processing and/or
 * prints
 * them directly to the console.
 */
class DefaultLogger {
  /**
   * @param logLevel    The maximal debug level of items to process
   * {debug|log|info|warn|error}
   */
  constructor(logLevel = 'log') {
    this.items = [];
    this.logLevel = '';
    this.logLevelHierarchy = void 0;
    this.directConsoleOut = true;
    this.logLevel = logLevel;
    this.logLevelHierarchy = this.getLogLevelHierarchyFromLogLevel(logLevel);
  }
  /**
   * Opens a log group.
   * All following log messages are grouped until groupEnd() is called.
   */
  group(groupName) {
    this.add('group', [groupName]);
    if (this.logLevelHierarchy >= LOGLEVEL_LOG) {
      this.consoleOut('group', [groupName]);
    }
  }
  /**
   * Closes a log group.
   * All messages after the last call of group() are grouped.
   */
  groupEnd(groupName) {
    this.add('groupEnd', [groupName]);
    if (this.logLevelHierarchy >= LOGLEVEL_LOG) {
      this.consoleOut('groupEnd', [groupName]);
    }
  }
  /**
   * Saves or prints logMessage marked as 'log'.
   */
  log(...args) {
    if (this.logLevelHierarchy >= LOGLEVEL_LOG) {
      const message = [...args];
      this.add('log', message);
      this.consoleOut('log', message);
    }
  }
  /**
   * Saves or prints logMessage marked as 'debug'.
   */
  debug(...args) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.log(...args);
  }
  /**
   * Saves or prints arguments marked as 'error'.
   */
  error(...args) {
    if (this.logLevelHierarchy >= LOGLEVEL_ERROR) {
      const message = [...args];
      this.add('error', message);
      this.consoleOut('error', message);
    }
  }
  /**
   * Saves or prints arguments marked as 'warn'.
   */
  warn(...args) {
    if (this.logLevelHierarchy >= LOGLEVEL_WARN) {
      const message = [...args];
      this.add('warn', message);
      this.consoleOut('warn', message);
    }
  }
  /**
   * Saves or prints arguments marked as 'info'.
   */
  info(...args) {
    if (this.logLevelHierarchy >= LOGLEVEL_INFO) {
      const message = [...args];
      this.add('info', message);
      this.consoleOut('info', message);
    }
  }
  /**
   * Returns logLevel hierarchy by log level
   */
  getLogLevelHierarchyFromLogLevel(logLevel) {
    let logLevelHierarchy = 0;
    switch (logLevel) {
      case 'silent':
        logLevelHierarchy = LOGLEVEL_SILENT;
        break;
      case 'log':
      case 'debug':
        logLevelHierarchy = LOGLEVEL_LOG;
        break;
      case 'info':
        logLevelHierarchy = LOGLEVEL_INFO;
        break;
      case 'warn':
        logLevelHierarchy = LOGLEVEL_WARN;
        break;
      case 'error':
        logLevelHierarchy = LOGLEVEL_ERROR;
        break;
      case 'group':
      case 'groupEnd':
        break;
      default:
        throw new Error('LogLevel ' + logLevel + ' not supported');
    }
    return logLevelHierarchy;
  }
  /**
   * Returns Array of LoggerItems
   */
  getBuffer() {
    return this.items;
  }
  /**
   * Returns logLevel
   */
  getLogLevel() {
    return this.logLevel;
  }
  /**
   * Returns logLevel hierarchy number
   */
  getLogLevelHierarchy() {
    return this.logLevelHierarchy;
  }
  /**
   * Disables the direct output to the console.
   * This way the output is handled by the parser alone.
   */
  disableConsoleOutput() {
    this.directConsoleOut = false;
  }
  /**
   * Enables the direct output to the console.
   * This is the default behavior.
   */
  enableConsoleOutput() {
    this.directConsoleOut = true;
  }
  /**
   * Allows a parser to output saved log messages.
   */
  out(parser) {
    parser.generateOutput(this.items);
  }
  /**
   * Prints a log message to the console without writing it to the log buffer.
   */
  consoleOut(logLevel, message) {
    // direct output only when no group is open
    if (this.directConsoleOut) {
      const toLog = message;
      switch (logLevel) {
        case 'silent':
          break;
        case 'log':
        case 'debug':
          console.log(...toLog);
          break;
        case 'info':
          console.log(...toLog);
          break;
        case 'warn':
          console.warn(...toLog);
          break;
        case 'error':
          console.error(...toLog);
          break;
        case 'group':
          console.log(...toLog);
          break;
        case 'groupEnd':
          console.log(...toLog);
          break;
        default:
          throw new Error('LogLevel ' + logLevel + ' not supported');
      }
    }
  }
  /**
   * Adds a LoggerItem to the items Array
   */
  add(logLevel, message) {
    this.items.push({
      logLevel,
      message
    });
  }
}

/**
 * Implements the Parser interface and stores logging output in logfile.
 */
class LogfileParser {
  constructor(fs) {
    this.fs = void 0;
    this.groupLevel = 0;
    this.posInGroup = 0;
    this.emptyGroup = 0;
    this.groupSeparatorSymbol = '';
    this.groupSeparatorCount = 0;
    this.items = [];
    this.logfile = 'domain.log';
    this.fs = fs;
  }
  /**
   * Iterates over an Array of LoggerItems.
   */
  generateOutput(items) {
    this.items = items;
    for (const item of items) {
      this.posInGroup++;
      this.out(item);
    }
  }
  /**
   * Overwrites the default logfile uri.
   */
  setLogfile(logfile) {
    this.logfile = logfile;
  }
  /**
   * Shows a separator line before each group.
   * Separator is build as symbol * count.
   */
  enableGroupSeparator(symbol = '-', count = 50) {
    this.groupSeparatorSymbol = symbol;
    this.groupSeparatorCount = count;
  }
  /**
   * Removes all content from the log file.
   *
   */
  emptyLogfile() {
    this.fs.writeFileSync(this.logfile, '');
  }
  /**
   * Stores LoggerItem messages into logfile with handling of group LoggerItems.
   */
  out(item) {
    const logLevel = item.logLevel;
    const message = item.message;
    if (item.logLevel === 'group') {
      this.groupLevel++;
    }
    const spacer = Array(this.groupLevel * 4).join(' ');
    if (this.groupLevel > 0) {
      switch (logLevel) {
        case 'group':
          if (!this.groupIsEmpty()) {
            if (this.groupSeparatorCount > 0) {
              const groupSeparator = Array(this.groupSeparatorCount).join(this.groupSeparatorSymbol);
              this.appendLogfile([groupSeparator], spacer.slice(0, -2));
            }
            this.appendLogfile(message, spacer);
          } else {
            this.emptyGroup++;
          }
          break;
        case 'groupEnd':
          if (this.emptyGroup === 0) {
            this.appendLogfile(message, spacer);
          } else {
            this.emptyGroup--;
          }
          break;
        default:
          this.appendLogfile(message, spacer);
          break;
      }
      if (item.logLevel === 'groupEnd') {
        this.groupLevel--;
      }
    } else {
      this.appendLogfile(message);
    }
  }
  /**
   * Appends log-content messages to logfile.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appendLogfile(message, spacer = '') {
    if (message.length === 1) {
      this.fs.appendFileSync(this.logfile, `${spacer}${message}\n`);
    } else {
      // TODO really JSON output here?
      this.fs.appendFileSync(this.logfile, spacer + JSON.stringify(message) + '\n');
    }
  }
  /**
   * Checks if an opened group contains loggerItems or other groups.
   */
  groupIsEmpty() {
    let msgFound = 0;
    let groupsOpen = 0;
    for (let i = this.posInGroup; i < this.items.length; i++) {
      const item = this.items[i];
      if (item.logLevel === 'group') {
        groupsOpen++;
      } else if (item.logLevel === 'groupEnd') {
        if (groupsOpen === 0) {
          break;
        }
        groupsOpen--;
      } else {
        msgFound = 1;
        break;
      }
    }
    if (msgFound === 1) {
      return false;
    }
    return true;
  }
}

/**
 * Generates a placeholder for a file to be downloaded
 */
const getPlaceholderConstantForFileAttachment = fileId => {
  if (!fileId) {
    throw new Error('fileId needs to be specified to generate placeholder');
  }
  return `{{FILE_ATTACHMENT_URL_${fileId.toUpperCase()}}}`;
};
const MAIL_CONSTANTS = {
  getPlaceholderConstantForFileAttachment
};

const forbiddenRegexCharacters = '±£$%^*_+§¡€#¢§¶•ªº«\\\\/<>?;|=';
/**
 * Regex pattern from the Marketing Cloud docs that describes the allowed characters for mandatory base fields.
 */
const marketingCloudBaseFieldValidPattern = new RegExp(`^[^${forbiddenRegexCharacters}]*$`);
/**
 * Regex pattern from the Marketing Cloud docs that describes the invalid characters for mandatory base fields.
 */
const marketingCloudBaseFieldInvalidPattern = new RegExp(`[${forbiddenRegexCharacters}]`, 'g');

var MarketingCloudResponseErrorType;
(function (MarketingCloudResponseErrorType) {
  MarketingCloudResponseErrorType["unknown"] = "unknown";
  /** The email address is unusable, e.g. because it is invalid or blocked/bounced */
  MarketingCloudResponseErrorType["emailAddressUnusable"] = "emailAddressUnusable";
  /** The data provided is invalid, e.g. because it is missing or not in the expected format */
  MarketingCloudResponseErrorType["dataInvalid"] = "dataInvalid";
  /** Error occured when querying the account id */
  MarketingCloudResponseErrorType["queryAccountIdError"] = "queryAccountIdError";
  /** Error occured when querying the token */
  MarketingCloudResponseErrorType["queryTokenError"] = "queryTokenError";
})(MarketingCloudResponseErrorType || (MarketingCloudResponseErrorType = {}));

/**
 * Rounds the value up after the given decimal.
 * Used the rounding method of y(x, m) = (x * m) / m
 * This is a fast implementation when the magnitude is known
 *
 * @param value Value to ceil
 * @param magnitude The power of 10 used to achieve certain precision
 */
const ceilBy = (value, magnitude = 1) => {
  return Math.ceil((value + Number.EPSILON) * magnitude) / magnitude;
};

/**
 * Rounds the value up after the given decimal.
 * This is a slower implementation than `ceilBy` and all its variants (ceil2, ceil3...)
 *
 * @param value Value to ceil
 * @param precision The amount of decimals to achieve
 */
const ceil = (value, precision = 0) => {
  return ceilBy(value, 10 ** precision);
};
/**
 * Rounds the value up after the first decimal.
 */
const ceil1 = value => {
  return ceilBy(value, 10);
};
/**
 * Rounds the value up after the second decimal.
 */
const ceil2 = value => {
  return ceilBy(value, 100);
};
/**
 * Rounds the value up after the third decimal.
 */
const ceil3 = value => {
  return ceilBy(value, 1000);
};
/**
 * Rounds the value up after the fourth decimal.
 */
const ceil4 = value => {
  return ceilBy(value, 10000);
};

const U = 0;
const V = 1;
const W = 2;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const checkRangeU = (list, u) => {
  if (!list) {
    throw new Error('checkRangeU list is not defined');
  }
  if (list.length === 0) {
    throw new Error('checkRangeU list.length is 0');
  }
  const allUnique = list.map(point => point[U]).filter((val, i, a) => a.indexOf(val) === i).sort((a, b) => a - b);
  const uMin = Math.min(...allUnique);
  const uMax = Math.max(...allUnique);
  if (u < uMin) {
    throw new Error('requested u is lower than the given range of the points');
  }
  if (u > uMax) {
    throw new Error('requested u is bigger than the given range of the points');
  }
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const checkRangeV = (list, v) => {
  if (!list) {
    throw new Error('checkRangeV list is not defined');
  }
  if (list.length === 0) {
    throw new Error('checkRangeV list.length is 0');
  }
  const allUnique = list.map(point => point[V]).filter((val, i, a) => a.indexOf(val) === i).sort((a, b) => a - b);
  const vMin = Math.min(...allUnique);
  const vMax = Math.max(...allUnique);
  if (v < vMin) {
    throw new Error('requested v is lower than the given range of the points');
  }
  if (v > vMax) {
    throw new Error('requested v is bigger than the given range of the points');
  }
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const checkRangeW = (list, w) => {
  if (!list) {
    throw new Error('checkRangeW list is not defined');
  }
  if (list.length === 0) {
    throw new Error('checkRangeW list.length is 0');
  }
  const allUnique = list.map(point => point[W]).filter((val, i, a) => a.indexOf(val) === i).sort((a, b) => a - b);
  const wMin = Math.min(...allUnique);
  const vMax = Math.max(...allUnique);
  if (w < wMin) {
    throw new Error('requested w is lower than the given range of the points');
  }
  if (w > vMax) {
    throw new Error('requested w is bigger than the given range of the points');
  }
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const getBiggerUValue = (list, u) => {
  if (!list) {
    throw new Error('getBiggerUValue list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getBiggerUValue list.length is 0');
  }
  return list.filter(point => point[U] >= u).sort((a, b) => a[U] - b[U])[0][U];
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const getLowerUValue = (list, u) => {
  if (!list) {
    throw new Error('getLowerUValue list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getLowerUValue list.length is 0');
  }
  return list.filter(point => point[U] <= u).sort((a, b) => a[U] - b[U]).reverse()[0][U];
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const getBiggerVValue = (list, v) => {
  if (!list) {
    throw new Error('getBiggerVValue list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getBiggerVValue list.length is 0');
  }
  return list.filter(point => point[V] >= v).sort((a, b) => a[V] - b[V])[0][V];
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const getLowerVValue = (list, v) => {
  if (!list) {
    throw new Error('getLowerVValue list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getLowerVValue list.length is 0');
  }
  return list.filter(point => point[V] <= v).sort((a, b) => a[V] - b[V]).reverse()[0][V];
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const getBiggerWValue = (list, w) => {
  if (!list) {
    throw new Error('getBiggerWValue list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getBiggerWValue list.length is 0');
  }
  return list.filter(point => point[W] >= w).sort((a, b) => a[W] - b[W])[0][W];
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const getLowerWValue = (list, w) => {
  if (!list) {
    throw new Error('getLowerWValue list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getLowerWValue list.length is 0');
  }
  return list.filter(point => point[W] <= w).sort((a, b) => a[W] - b[W]).reverse()[0][W];
};
const getBiggerUPoints = (list, u) => {
  if (!list) {
    throw new Error('getBiggerUPoints list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getBiggerUPoints list.length is 0');
  }
  const biggerU = getBiggerUValue(list, u);
  if (typeof biggerU === 'undefined') {
    console.log('BÄH biggerU in getBiggerUPoints is undefined');
  }
  return list.filter(point => point[U] >= biggerU).sort((a, b) => a[U] - b[U]);
};
const getNextBiggerUPoints = (list, u) => {
  if (list.length === 0) {
    return [];
  }
  const biggerU = getBiggerUValue(list, u);
  if (typeof biggerU === 'undefined') {
    throw new Error('getNextBiggerUPoints  biggerU is undefined');
  }
  return list.filter(point => point[U] === biggerU).sort((a, b) => a[U] - b[U]);
};
const getLowerUPoints = (list, u) => {
  if (list.length === 0) {
    return [];
  }
  const lowerU = getLowerUValue(list, u);
  if (typeof lowerU === 'undefined') {
    throw new Error('getLowerUPoints  lowerU is undefined');
  }
  return list.filter(point => point[U] <= lowerU).sort((a, b) => a[U] - b[U]).reverse();
};
const getNextLowerUPoints = (list, u) => {
  if (list.length === 0) {
    return [];
  }
  const lowerU = getLowerUValue(list, u);
  if (typeof lowerU === 'undefined') {
    throw new Error('getNextLowerUPoints lowerU is undefined');
  }
  return list.filter(point => point[U] === lowerU).sort((a, b) => a[U] - b[U]).reverse();
};
const getBiggerVPoints = (list, v) => {
  if (list.length === 0) {
    return [];
  }
  const biggerV = getBiggerVValue(list, v);
  if (typeof biggerV === 'undefined') {
    throw new Error('getBiggerVPoints biggerV is undefined');
  }
  return list.filter(point => point[V] >= biggerV).sort((a, b) => a[V] - b[V]);
};
const getNextBiggerVPoints = (list, v) => {
  if (list.length === 0) {
    return [];
  }
  const biggerV = getBiggerVValue(list, v);
  if (typeof biggerV === 'undefined') {
    throw new Error('getNextBiggerVPoints biggerV is undefined');
  }
  return list.filter(point => point[V] === biggerV).sort((a, b) => a[V] - b[V]);
};
const getLowerVPoints = (list, v) => {
  if (list.length === 0) {
    return [];
  }
  const lowerV = getLowerVValue(list, v);
  if (typeof lowerV === 'undefined') {
    throw new Error('getLowerVPoints lowerV is undefined');
  }
  return list.filter(point => point[V] <= lowerV).sort((a, b) => a[V] - b[V]).reverse();
};
const getNextLowerVPoints = (list, v) => {
  if (list.length === 0) {
    return [];
  }
  const lowerV = getLowerVValue(list, v);
  if (typeof lowerV === 'undefined') {
    throw new Error('getNextLowerVPoints lowerV is undefined');
  }
  return list.filter(point => point[V] === lowerV).sort((a, b) => a[V] - b[V]).reverse();
};
const getBiggerWPoints = (list, w) => {
  if (list.length === 0) {
    return [];
  }
  const biggerW = getBiggerWValue(list, w);
  if (typeof biggerW === 'undefined') {
    throw new Error('getBiggerWPoints biggerW is undefined');
  }
  return list.filter(point => point[W] >= biggerW).sort((a, b) => a[W] - b[W]);
};
const getNextBiggerWPoints = (list, w) => {
  if (list.length === 0) {
    return [];
  }
  const biggerW = getBiggerWValue(list, w);
  if (typeof biggerW === 'undefined') {
    throw new Error('getNextBiggerWPoints biggerW is undefined');
  }
  return list.filter(point => point[W] === biggerW).sort((a, b) => a[W] - b[W]);
};
const getLowerWPoints = (list, w) => {
  if (list.length === 0) {
    return [];
  }
  const lowerW = getLowerWValue(list, w);
  if (typeof lowerW === 'undefined') {
    throw new Error('getLowerWPoints lowerW is undefined');
  }
  return list.filter(point => point[W] <= lowerW).sort((a, b) => a[W] - b[W]).reverse();
};
const getNextLowerWPoints = (list, w) => {
  if (list.length === 0) {
    return [];
  }
  const lowerW = getLowerWValue(list, w);
  if (typeof lowerW === 'undefined') {
    throw new Error('getNextLowerWPoints lowerW is undefined');
  }
  return list.filter(point => point[W] === lowerW).sort((a, b) => a[W] - b[W]).reverse();
};
const getBiggerUPoint = (list, u) => {
  if (!list) {
    throw new Error('getBiggerUPoint list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getBiggerUPoint list.length is 0');
  }
  return getBiggerUPoints(list, u)[0];
};
const getLowerUPoint = (list, u) => {
  if (!list) {
    throw new Error('getLowerUPoint list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getLowerUPoint list.length is 0');
  }
  return getLowerUPoints(list, u)[0];
};
const getBiggerVPoint = (list, v) => {
  if (!list) {
    throw new Error('getBiggerVPoint list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getBiggerVPoint list.length is 0');
  }
  return getBiggerVPoints(list, v)[0];
};
const getLowerVPoint = (list, v) => {
  if (!list) {
    throw new Error('getLowerVPoint list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getLowerVPoint list.length is 0');
  }
  return getLowerVPoints(list, v)[0];
};
const getBiggerWPoint = (list, w) => {
  if (!list) {
    throw new Error('getBiggerWPoint list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getBiggerWPoint list.length is 0');
  }
  return getBiggerWPoints(list, w)[0];
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const getLowerWPoint = (list, w) => {
  if (!list) {
    throw new Error('getLowerWPoint list is not defined');
  }
  if (list.length === 0) {
    throw new Error('getLowerWPoint list.length is 0');
  }
  return getLowerWPoints(list, w)[0];
};
const point1DEqual = (p1, p2) => {
  if (!p1 || !p2) {
    return false;
  }
  const Y = 1;
  if (p1[U] === p2[U]) {
    if (p1[Y] !== p2[Y]) {
      throw new Error('Dimensions of p1 and p2 are the same but Y is not!');
    }
    return true;
  }
  return false;
};
const point2DEqual = (p1, p2) => {
  if (!p1 || !p2) {
    return false;
  }
  const Y = 2;
  if (p1[U] === p2[U] && p1[V] === p2[V]) {
    if (p1[Y] !== p2[Y]) {
      throw new Error('Dimensions of p1 and p2 are the same but Y is not!');
    }
    return true;
  }
  return false;
};
const point3DEqual = (p1, p2) => {
  if (!p1 || !p2) {
    return false;
  }
  const Y = 3;
  if (p1[U] === p2[U] && p1[V] === p2[V] && p1[W] === p2[W]) {
    if (p1[Y] !== p2[Y]) {
      throw new Error('Dimensions of p1 and p2 are the same but Y is not!');
    }
    return true;
  }
  return false;
};

/*
 * linearInterpolation1D
 *
 * does a linear interpolation for two given points with one dimension u, where
 * u can be any (real) value.
 * This function also calculates values for u not between p1.u and p2.u.
 */
const linearInterpolation1D = (p1, p2, u) => {
  const Y = 1;
  if (typeof p1 === 'undefined') {
    throw new Error('p1 is not defined');
  }
  if (typeof p2 === 'undefined') {
    throw new Error('p2 is not defined');
  }
  if (typeof u === 'undefined') {
    throw new Error('u is not defined');
  }
  // when the points are the same then the resulting point has the same values
  if (point1DEqual(p1, p2) && p1[U] === u) {
    return p1;
  }
  const du = p1[U] - p2[U];
  const dy = p1[Y] - p2[Y];
  // console.log("Δu : ", du);
  // console.log("Δy : ", dy);
  const m = dy / du;
  // console.log("m  : ", m);
  const b = p1[Y] - m * p1[U];
  // console.log("b  : ", b);
  const y = m * u + b;
  // console.log('y = m * u + b     = ', m, '* u', Math.sign(b) >= 0 ? '+' : '-', Math.abs(b));
  // console.log('y(', u, ') = ', y);
  return [u, y];
};
/**
 * linearInterpolationBySection1D
 *
 * does a section wise interpolation by using linearInterpolation1D()
 *
 * In contrast to linearInterpolation1D() this function only calculates
 * values where dimension u is in the range of the given points.
 */
const linearInterpolationBySection1D = (list, u) => {
  if (typeof list === 'undefined') {
    throw new Error('list is not defined');
  }
  if (typeof u === 'undefined') {
    throw new Error('u is not defined');
  }
  // No points, no interpolation.
  if (list.length === 0) {
    throw new Error('list is empty');
  }
  // If there is an entry for the requested u just return it without interpolation.
  // This is also the only case where one point in the list is sufficient.
  const matchingPoint = list.filter(pD1 => pD1[U] === u);
  if (matchingPoint[0]) {
    return matchingPoint[0];
  }
  if (list.length < 2) {
    throw new Error('at least two points are required');
  }
  checkRangeU(list, u);
  const pointBiggerU = getBiggerUPoint(list, u);
  const pointLowerU = getLowerUPoint(list, u);
  return linearInterpolation1D(pointBiggerU, pointLowerU, u);
};

/*
 * linearInterpolation2DfixV
 *
 * does a linear interpolation for two given points with two dimensions (u,v).
 *
 * p1.v and p2.v must be the same
 *
 * The calculation of u between p1.u and p2.u returns useful values.
 * (If not more measurements will be help.)
 *
 * This function also calculates values for u not between p1.u and p2.u.
 * So the more away u is from that range, the bigger the possible error will be.
 *
 */
const linearInterpolation2DfixV = (p1, p2, u) => {
  const Y = 2;
  if (typeof p1 === 'undefined') {
    throw new Error('p1 is not defined');
  }
  if (typeof p2 === 'undefined') {
    throw new Error('p2 is not defined');
  }
  if (typeof u === 'undefined') {
    throw new Error('u is not defined');
  }
  // when the points are the same then the resulting point has the same values
  if (point2DEqual(p1, p2) && p1[U] === u) {
    return p1;
  }
  if (p1[U] === p2[U]) {
    throw new Error('p1 and p2 have the same u value so the slope can not be calculated');
  }
  if (p1[V] !== p2[V]) {
    throw new Error('p1 and p2 do not have the same v value');
  }
  const du = p1[U] - p2[U];
  const dy = p1[Y] - p2[Y];
  // console.log("Δu : ", du);
  // console.log("Δy : ", dy);
  const m = dy / du;
  // console.log("m  : ", m);
  const b = p1[Y] - m * p1[U];
  // console.log("b  : ", b);
  const y = m * u + b;
  // console.log('y = m * u + b     = ', m, '* u', Math.sign(b) >= 0 ? '+' : '-', Math.abs(b));
  // console.log('y(', u, ') = ', y);
  return [u, p1[V], y];
};
/*
 * linearInterpolation2DfixU
 *
 * does a linear interpolation for two given points with two dimensions (u,v).
 *
 * p1.u and p2.u must be the same
 *
 * The calculation of u between p1.v and p2.v returns useful values.
 * (If not more measurements will be help.)
 *
 * This function also calculates values for v not between p1.v and p2.v.
 * So the more away v is from that range, the bigger the possible error will be.
 */
const linearInterpolation2DfixU = (p1, p2, v) => {
  const Y = 2;
  if (typeof p1 === 'undefined') {
    throw new Error('p1 is not defined');
  }
  if (typeof p2 === 'undefined') {
    throw new Error('p2 is not defined');
  }
  if (typeof v === 'undefined') {
    throw new Error('v is not defined');
  }
  // when the points are the same then the resulting point has the same values
  if (point2DEqual(p1, p2) && p1[V] === v) {
    return p1;
  }
  if (p1[V] === p2[V]) {
    throw new Error('p1 and p2 have the same v value so the slope can not be calculated');
  }
  if (p1[U] !== p2[U]) {
    throw new Error('p1 and p2 do not have the same u value');
  }
  const dv = p1[V] - p2[V];
  const dy = p1[Y] - p2[Y];
  // console.log("Δv : ", dv);
  // console.log("Δy : ", dy);
  const m = dy / dv;
  // console.log("m  : ", m);
  const b = p1[Y] - m * p1[V];
  // console.log("b  : ", b);
  const y = m * v + b;
  // console.log('y = m * v + b     = ', m, '* v', Math.sign(b) >= 0 ? '+' : '-', Math.abs(b));
  // console.log('y(', v, ') = ', y);
  return [p1[U], v, y];
};
/**
 * linearInterpolationBySection2D
 *
 * does a section wise interpolation by using linearInterpolation2DconstV() and linearInterpolation2DconstU()
 *
 * In contrast to linearInterpolation2D() this function only calculates
 * values where the dimensions u and  v are in the range of the given points.
 */
const linearInterpolationBySection2D = (list, u, v) => {
  if (typeof list === 'undefined') {
    throw new Error('list is not defined');
  }
  if (typeof u === 'undefined') {
    throw new Error('u is not defined');
  }
  if (typeof v === 'undefined') {
    throw new Error('v is not defined');
  }
  // No points, no interpolation.
  if (list.length === 0) {
    throw new Error('list is empty');
  }
  // If there is an entry for the requested u,v just return it without interpolation.
  // This is also the only case where one point in the list is sufficient.
  const matchingPoint = list.filter(p2D => p2D[U] === u && p2D[V] === v);
  if (matchingPoint[0]) {
    return matchingPoint[0];
  }
  checkRangeU(list, u);
  checkRangeV(list, v);
  // get all values of the two v lines
  const pointsBiggerV = getNextBiggerVPoints(list, v);
  const pointsLowerV = getNextLowerVPoints(list, v);
  // get the four next points on the v lines
  const pointsBiggerVBiggerU = getBiggerUPoint(pointsBiggerV, u);
  const pointsBiggerVLowerU = getLowerUPoint(pointsBiggerV, u);
  const pointsLowerVBiggerU = getBiggerUPoint(pointsLowerV, u);
  const pointsLowerVLowerU = getLowerUPoint(pointsLowerV, u);
  // calculate the two resulting points on the v line
  const pointBiggerVNewU = linearInterpolation2DfixV(pointsBiggerVBiggerU, pointsBiggerVLowerU, u);
  const pointLowerVNewU = linearInterpolation2DfixV(pointsLowerVBiggerU, pointsLowerVLowerU, u);
  // calculate the resulting point between the v lines
  return linearInterpolation2DfixU(pointBiggerVNewU, pointLowerVNewU, v);
};

/**
 * Rounds the value after the given decimal.
 * Used the rounding method of y(x, m) = (x * m) / m
 * This is a fast implementation when the magnitude is known
 *
 * @param value Value to round
 * @param magnitude The power of 10 used to achieve certain precision
 */
const roundBy = (value, magnitude = 1) => {
  return Math.round((value + Number.EPSILON) * magnitude) / magnitude;
};

/**
 * Rounds the value after the given decimal.
 * This is a slower implementation than `roundBy` and all its variants (round1, round2...)
 *
 * @param value - Value to round
 * @param precision - The amount of decimal to round with
 */
const round = (value, precision = 0) => {
  return roundBy(value, 10 ** precision);
};
/**
 * Round passed value with one digit after the decimal point
 */
const round1 = value => {
  return roundBy(value, 10);
};
/**
 * Round passed value with two digits after the decimal point
 */
const round2 = value => {
  return roundBy(value, 100);
};
/**
 * Round passed value with three digits after the decimal point
 */
const round3 = value => {
  return roundBy(value, 1000);
};
/**
 * Round passed value with four digits after the decimal point
 */
const round4 = value => {
  return roundBy(value, 10000);
};
/**
 * Round passed value with five digits after the decimal point
 */
const round5 = value => {
  return roundBy(value, 100000);
};
/**
 * Round passed value with ten digits after the decimal point
 */
const round10 = value => {
  return roundBy(value, 10000000000);
};

/**
 * calculate a straight line
 *
 *   y = m * x +b
 *
 * given by two points (x,y) and an additional x value.
 *
 * For completeness the units are passed to be shown in the journal.
 */
const calcStraight = (x1, x2, y1, y2, x, xFormulaSymbol, xUnit, yFormulaSymbol, yUnit) => {
  const journal = new ExplanationJournal();
  journal.addTitle('calcStraight ' + yFormulaSymbol + '[' + yUnit + '](' + xFormulaSymbol + '[' + xUnit + '])');
  journal.addKeyValue('u', x, xUnit);
  const mUnit = yUnit === xUnit ? '' : yUnit + ' / ' + xUnit;
  const bUnit = yUnit;
  const dx = x1 - x2;
  journal.addKeyValue('Δx', 'x1 - x2');
  journal.addKeyValue('', `${x1} ${xUnit} - ${x2}`, xUnit);
  journal.addKeyValue('', dx, xUnit);
  journal.addText('\n');
  const dy = round10(y1 - y2);
  journal.addKeyValue('Δy', 'y1 - y2');
  journal.addKeyValue('', `${y1} ${yUnit} - ${y2}`, yUnit);
  journal.addKeyValue('', dy, yUnit);
  journal.addText('\n');
  const m = round10(dy / dx);
  journal.addVisualFraction(['m', m, mUnit], [['Δy', dy, yUnit]], [['Δx', dx, xUnit]]);
  const b = round10(y1 - m * x1);
  journal.addKeyValue('b', 'y1 - m * x1');
  journal.addKeyValue('', `${y1} ${yUnit} - ${m} ${mUnit} * ${x1} ${xUnit}`);
  journal.addKeyValue('', b, bUnit);
  journal.addText('\n');
  const y = round5(m * x + b);
  journal.addKeyValue('y', 'm * x + b');
  journal.addKeyValue('', `${m} ${mUnit} * ${x} ${xUnit}${Math.sign(b) >= 0 ? ' + ' : ' - '}${Math.abs(b)} ${bUnit}`);
  journal.addKeyValue('', y, yUnit);
  return {
    y,
    m,
    b,
    explanation: journal.getJournal()
  };
};

/*
 * linearInterpolation3DfixVW
 *
 * does a linear interpolation for two given points with three dimensions u,v, w where
 * v and w are fix.
 * This function also calculates values for u not between p1.u and p2.u.
 */
const linearInterpolation3DfixVW = (p1, p2, u) => {
  const journal = new ExplanationJournal();
  journal.addTitle('linearInterpolation3DfixVW()');
  const Y = 3;
  if (typeof p1 === 'undefined') {
    throw new Error('p1 is not defined');
  }
  if (typeof p2 === 'undefined') {
    throw new Error('p2 is not defined');
  }
  if (typeof u === 'undefined') {
    throw new Error('u is not defined');
  }
  journal.addHeadline('parameter:');
  journal.addPoint3D('point 1    ', p1);
  journal.addPoint3D('point 2    ', p2);
  journal.addKeyValue('u (p)', u);
  // when the points are the same then the resulting point has the same values
  if (point3DEqual(p1, p2) && p1[U] === u) {
    journal.addText('points p1 and p2 defining the line are the same, and have the requested u value, so return that point');
    journal.addKeyValue('p1', p1.toString());
    return {
      point: p1,
      explanation: journal.getJournal()
    };
  }
  if (p1[U] === p2[U]) {
    throw new Error('p1 and p2 have the same u value so the slope can not be calculated');
  }
  if (p1[V] !== p2[V]) {
    throw new Error('p1 and p2 do not have the same v value');
  }
  if (p1[W] !== p2[W]) {
    throw new Error('p1 and p2 do not have the same w value');
  }
  const straightResult = calcStraight(p1[U], p2[U], p1[Y], p2[Y], u, 'p', 'MPa', 'W', 'nm/m');
  journal.addText(straightResult.explanation, 4);
  return {
    point: [u, p1[V], p1[W], round5(straightResult.y)],
    explanation: journal.getJournal()
  };
};
/*
 * linearInterpolation3DfixUW
 *
 * does a linear interpolation for two given points with three dimensions u,v, w where
 * u and w are fix.
 * This function also calculates values for v not between p1.v and p2.v.
 */
const linearInterpolation3DfixUW = (p1, p2, v) => {
  const journal = new ExplanationJournal();
  journal.addTitle('linearInterpolation3DfixUW()');
  const Y = 3;
  if (typeof p1 === 'undefined') {
    throw new Error('p1 is not defined');
  }
  if (typeof p2 === 'undefined') {
    throw new Error('p2 is not defined');
  }
  if (typeof v === 'undefined') {
    throw new Error('v is not defined');
  }
  journal.addHeadline('parameter:');
  journal.addPoint3D('point 1    ', p1);
  journal.addPoint3D('point 2    ', p2);
  journal.addKeyValue('v (v)', v);
  // when the points are the same then the resulting point has the same values
  if (point3DEqual(p1, p2) && p1[V] === v) {
    journal.addText('points p1 and p2 defining the line are the same, and have the requested v value, so return that point');
    return {
      point: p1,
      explanation: journal.getJournal()
    };
  }
  if (p1[V] === p2[V]) {
    throw new Error('p1 and p2 have the same v value so the slope can not be calculated');
  }
  if (p1[U] !== p2[U]) {
    throw new Error('p1 and p2 do not have the same u value');
  }
  if (p1[W] !== p2[W]) {
    throw new Error('p1 and p2 do not have the same w value');
  }
  const straightResult = calcStraight(p1[V], p2[V], p1[Y], p2[Y], v, 'v', 'm/s', 'W', 'nm/m');
  journal.addText(straightResult.explanation, 4);
  return {
    point: [p1[U], v, p1[W], round5(straightResult.y)],
    explanation: journal.getJournal()
  };
};
/*
 * linearInterpolation3DfixUV
 *
 * does a linear interpolation for two given points with three dimensions u,v, w where
 * u and v are fix.
 * This function also calculates values for w not between p1.w and p2.w.
 */
const linearInterpolation3DfixUV = (p1, p2, w) => {
  const journal = new ExplanationJournal();
  journal.addTitle('linearInterpolation3DfixUV()');
  journal.addHeadline('parameter:');
  const Y = 3;
  if (typeof p1 === 'undefined') {
    throw new Error('p1 is not defined');
  }
  if (typeof p2 === 'undefined') {
    throw new Error('p2 is not defined');
  }
  if (typeof w === 'undefined') {
    throw new Error('w is not defined');
  }
  journal.addPoint3D('point 1    ', p1);
  journal.addPoint3D('point 2    ', p2);
  journal.addKeyValue('w (T)', w);
  // when the points are the same then the resulting point has the same values
  if (point3DEqual(p1, p2) && p1[W] === w) {
    journal.addText('points p1 and p2 defining the line are the same, and have the requested w value, so return that point');
    return {
      point: p1,
      explanation: journal.getJournal()
    };
  }
  if (p1[W] === p2[W]) {
    throw new Error('p1 and p2 have the same w value so the slope can not be calculated');
  }
  if (p1[U] !== p2[U]) {
    throw new Error('p1 and p2 do not have the same u value');
  }
  if (p1[V] !== p2[V]) {
    throw new Error('p1 and p2 do not have the same v value');
  }
  const straightResult = calcStraight(p1[W], p2[W], p1[Y], p2[Y], w, 'T', '°C', 'W', 'nm/m');
  journal.addText(straightResult.explanation, 4);
  return {
    point: [p1[U], p1[V], w, round5(straightResult.y)],
    explanation: journal.getJournal()
  };
};
/**
 * linearInterpolationBySection3D
 *
 * does a section wise interpolation by using linearInterpolation3D()
 *
 * In contrast to linearInterpolation2D...() this function only calculates
 * values where the dimensions u, v and w are in the range of the given points.
 *
 * At the moment there is no differentiation if points are the same, so the calculation
 * may be to complicated in some cases. I will add shortcuts for that later on.
 */
const linearInterpolationBySection3D = (list, u, v, w) => {
  const Y = 3;
  const journal = new ExplanationJournal();
  journal.addTitle('linearInterpolationBySection3D()');
  if (typeof list === 'undefined') {
    throw new Error('list is not defined');
  }
  if (typeof u === 'undefined') {
    throw new Error('u is not defined');
  }
  if (typeof v === 'undefined') {
    throw new Error('v is not defined');
  }
  if (typeof w === 'undefined') {
    throw new Error('w is not defined');
  }
  // No points, no interpolation.
  if (list.length === 0) {
    throw new Error('list is empty');
  }
  journal.addHeadline('parameter:');
  journal.addKeyValue('u (SP)', u);
  journal.addKeyValue('v (v) ', v);
  journal.addKeyValue('w (T) ', w);
  journal.addPoint3DList('data', list);
  // If there is an entry for the requested u,v,w then just return it without interpolation.
  // This is also the only case where one point in the list is sufficient.
  const matchingPoint = list.filter(point => {
    return point[U] === u && point[V] === v && point[W] === w;
  });
  if (matchingPoint[0]) {
    journal.addPoint3D('found existing measure point:', matchingPoint[0]);
    return {
      point: matchingPoint[0],
      explanation: journal.getJournal()
    };
  }
  /*
  const reducedUList = list.filter((e) => e[U] === u);
  const reducedVList = list.filter((e) => e[V] === v);
  const reducedWList = list.filter((e) => e[W] === w);
     logger.addPoint3DList('reducedUList', reducedUList);
  logger.addPoint3DList('reducedVList', reducedVList);
  logger.addPoint3DList('reducedWList', reducedWList);
  */
  // Fixme: these are only absolute checks
  //        maybe I should add also
  //          checkRangeUV(list, u, v);
  //        and
  //          checkRangeUVW(list, u, v, w);
  //        to check the available ranges in relation to the other dimensions
  checkRangeU(list, u);
  checkRangeV(list, v);
  checkRangeW(list, w);
  // shortcut
  const reducedVWList = list.filter(e => e[V] === v && e[W] === w);
  journal.addPoint3DList('reducedVWList', reducedVWList);
  if (reducedVWList.length > 1) {
    const allUnique = reducedVWList.map(point => point[U]).filter((val, i, a) => a.indexOf(val) === i).sort((a, b) => a - b);
    const uMin = Math.min(...allUnique);
    const uMax = Math.max(...allUnique);
    journal.addKeyValue('uMin', uMin);
    journal.addKeyValue('uMax', uMax);
    journal.addKeyValue('u', u);
    if (u < uMin || u > uMax) {
      journal.addText('Solution is not possible, because of too few measure points for U.');
      if (u < uMin) {
        journal.addText('Find a solution for a lower value left from the existing points. ( u < uMin) \n');
        const pointsBiggerU = getBiggerUPoints(reducedVWList, u);
        const p1 = pointsBiggerU[0];
        const p2 = pointsBiggerU[1];
        journal.addPoint3D('p1', p1);
        journal.addPoint3D('p2', p2);
        const straightResult = calcStraight(p1[U], p2[U], p1[Y], p2[Y], u, 'p', 'MPa', 'y', 'nm/m');
        journal.addSubText(straightResult.explanation);
        return {
          point: [u, v, w, straightResult.y],
          explanation: journal.getJournal()
        };
      } else {
        journal.addText('Find a solution for a higher value right from the existing points. ( u > uMax)\n');
        const pointsLowerU = getLowerUPoints(reducedVWList, u);
        const p1 = pointsLowerU[0];
        const p2 = pointsLowerU[1];
        journal.addPoint3D('p1', p1);
        journal.addPoint3D('p2', p2);
        const straightResult = calcStraight(p1[U], p2[U], p1[Y], p2[Y], u, 'p', 'Mpa', 'y', 'nm/m');
        journal.addSubText(straightResult.explanation);
        return {
          point: [u, v, w, straightResult.y],
          explanation: journal.getJournal()
        };
      }
    }
  }
  journal.addHeadline('get all values of the two w areas:');
  const pointsBiggerW = getNextBiggerWPoints(list, w);
  const pointsLowerW = getNextLowerWPoints(list, w);
  journal.addPoint3DList('pointsBiggerW', pointsBiggerW);
  journal.addPoint3DList('pointsLowerW', pointsLowerW);
  journal.addLinebreak();
  journal.addHeadline('get all points of the four v lines:');
  const pointsBiggerWBiggerV = getNextBiggerVPoints(pointsBiggerW, v);
  const pointsBiggerWLowerV = getNextLowerVPoints(pointsBiggerW, v);
  journal.addPoint3DList('pointsBiggerWBiggerV', pointsBiggerWBiggerV);
  journal.addPoint3DList('pointsBiggerWLowerV', pointsBiggerWLowerV);
  journal.addLinebreak();
  const pointsLowerWBiggerV = getNextBiggerVPoints(pointsLowerW, v);
  const pointsLowerWLowerV = getNextLowerVPoints(pointsLowerW, v);
  journal.addPoint3DList('pointsLowerWBiggerV', pointsLowerWBiggerV);
  journal.addPoint3DList('pointsLowerWLowerV', pointsLowerWLowerV);
  journal.addLinebreak();
  journal.addHeadline('get the eight next points on the v lines:');
  const pointsBiggerWBiggerVBiggerU = getBiggerUPoint(pointsBiggerWBiggerV, u);
  const pointsBiggerWBiggerVLowerU = getLowerUPoint(pointsBiggerWBiggerV, u);
  journal.addPoint3D('pointsBiggerWBiggerVBiggerU', pointsBiggerWBiggerVBiggerU);
  journal.addPoint3D('pointsBiggerWBiggerVLowerU', pointsBiggerWBiggerVLowerU);
  journal.addLinebreak();
  const pointsBiggerWLowerVBiggerU = getBiggerUPoint(pointsBiggerWLowerV, u);
  const pointsBiggerWLowerVLowerU = getLowerUPoint(pointsBiggerWLowerV, u);
  journal.addPoint3D('pointsBiggerWLowerVBiggerU', pointsBiggerWLowerVBiggerU);
  journal.addPoint3D('pointsBiggerWLowerVLowerU', pointsBiggerWLowerVLowerU);
  journal.addLinebreak();
  const pointsLowerWBiggerVBiggerU = getBiggerUPoint(pointsLowerWBiggerV, u);
  const pointsLowerWBiggerVLowerU = getLowerUPoint(pointsLowerWBiggerV, u);
  journal.addPoint3D('pointsLowerWBiggerVBiggerU', pointsLowerWBiggerVBiggerU);
  journal.addPoint3D('pointsLowerWBiggerVLowerU', pointsLowerWBiggerVLowerU);
  journal.addLinebreak();
  const pointsLowerWLowerVBiggerU = getBiggerUPoint(pointsLowerWLowerV, u);
  const pointsLowerWLowerVLowerU = getLowerUPoint(pointsLowerWLowerV, u);
  journal.addPoint3D('pointsLowerWLowerVBiggerU', pointsLowerWLowerVBiggerU);
  journal.addPoint3D('pointsLowerWLowerVLowerU', pointsLowerWLowerVLowerU);
  journal.addLinebreak();
  journal.addHeadline('calculate the four resulting points on the v line:');
  const pointBiggerWBiggerVNewU = linearInterpolation3DfixVW(pointsBiggerWBiggerVBiggerU, pointsBiggerWBiggerVLowerU, u);
  journal.addText(pointBiggerWBiggerVNewU.explanation, 4);
  const pointBiggerWLowerVNewU = linearInterpolation3DfixVW(pointsBiggerWLowerVBiggerU, pointsBiggerWLowerVLowerU, u);
  journal.addText(pointBiggerWLowerVNewU.explanation, 4);
  const pointLowerWBiggerVNewU = linearInterpolation3DfixVW(pointsLowerWBiggerVBiggerU, pointsLowerWBiggerVLowerU, u);
  journal.addText(pointLowerWBiggerVNewU.explanation, 4);
  const pointLowerWLowerVNewU = linearInterpolation3DfixVW(pointsLowerWLowerVBiggerU, pointsLowerWLowerVLowerU, u);
  journal.addText(pointLowerWLowerVNewU.explanation, 4);
  journal.addHeadline('calculate the two resulting points between the v line:');
  const pointBiggerWNewUV = linearInterpolation3DfixUW(pointBiggerWBiggerVNewU.point, pointBiggerWLowerVNewU.point, v);
  journal.addText(pointBiggerWNewUV.explanation, 4);
  const pointLowerWNewUV = linearInterpolation3DfixUW(pointLowerWBiggerVNewU.point, pointLowerWLowerVNewU.point, v);
  journal.addText(pointLowerWNewUV.explanation, 4);
  journal.addHeadline('calculate the resulting point between the w areas:');
  const r = linearInterpolation3DfixUV(pointBiggerWNewUV.point, pointLowerWNewUV.point, w);
  journal.addText(r.explanation, 4);
  return {
    point: r.point,
    explanation: journal.getJournal()
  };
};

/**
 * Set points that are obvious from a physical perspective.
 */
const guessAdditionalPoints = realPoints => {
  // add points for P = 0
  const zeroPointsP = [];
  for (const point of realPoints) {
    const v = point[1];
    const t = point[2];
    if (!zeroPointsP.some(elem => elem[1] === v && elem[2] === t)) {
      zeroPointsP.push([0, v, t, 0]);
    }
  }
  // add points for v = 0
  const zeroPointsV = [];
  for (const point of realPoints) {
    const p = point[0];
    const t = point[2];
    if (!zeroPointsV.some(elem => elem[0] === p && elem[2] === t)) {
      zeroPointsV.push([p, 0, t, 0]);
    }
  }
  // console.log('zeroPointsP');
  // console.log(zeroPointsP);
  //
  // console.log('zeroPointsV');
  // console.log(zeroPointsV);
  const allZeroPoints = zeroPointsP.concat(zeroPointsV);
  const allPoints = realPoints.concat(allZeroPoints);
  const maxPointsP = [];
  // now pointsZero contains all existing v-t combinations
  for (const zeropointP of zeroPointsP) {
    const v = zeropointP[1];
    const t = zeropointP[2];
    const matchingPointsVT = allPoints.filter(point => point[1] === v && point[2] === t).sort((pa, pb) => pb[0] - pa[0]);
    const pMaxValueP = matchingPointsVT[0][0];
    const pMaxValueW = matchingPointsVT[0][3];
    // console.log('pMaxValueP : ', pMaxValueP);
    // console.log('pMaxValueW : ', pMaxValueW);
    // console.log();
    const pMax = 100; // unit: MPa;
    const valueWforPmax = linearInterpolation1D([0, 0], [pMaxValueP, pMaxValueW], pMax);
    maxPointsP.push([pMax, v, t, round5(valueWforPmax[1])]);
  }
  // console.log('maxPointsP');
  // console.log(maxPointsP);
  const maxPointsV = [];
  // now pointsZero contains all existing p-t combinations
  for (const zeropointV of zeroPointsV) {
    // console.log('pz : ', pz);
    const p = zeropointV[0];
    const t = zeropointV[2];
    const matchingPointsPT = allPoints.filter(point => point[0] === p && point[2] === t).sort((pa, pb) => pb[1] - pa[1]);
    const vMaxValueV = matchingPointsPT[0][1];
    const vMaxValueW = matchingPointsPT[0][3];
    // console.log('pMaxValueP : ', pMaxValueP);
    // console.log('pMaxValueW : ', pMaxValueW);
    // console.log();
    const vMax = 5; // unit: m/s;
    const valueWforVmax = linearInterpolation1D([0, 0], [vMaxValueV, vMaxValueW], vMax);
    maxPointsV.push([p, vMax, t, round5(valueWforVmax[1])]);
  }
  // console.log('maxPointsV');
  // console.log(maxPointsV);
  return allZeroPoints.concat(maxPointsP).concat(maxPointsV);
};

/**
 * Truncates the value after the given decimal.
 * Used the rounding method of y(x, m) = (x * m) / m
 * This is a fast implementation when the magnitude is known
 *
 * @param value Value to truncate
 * @param magnitude The power of 10 used to achieve certain precision
 */
const truncBy = (value, magnitude = 1) => {
  return Math.trunc((value + Number.EPSILON) * magnitude) / magnitude;
};

/**
 * Truncates the value after the given decimal.
 * This is a slower implementation than `truncBy` and all its variants (trunc1, trunc2...)
 *
 * @param value Value to truncate
 * @param precision The amount of decimals to achieve
 */
const trunc = (value, precision = 0) => {
  return truncBy(value, 10 ** precision);
};
/**
 * Truncates passed value with one digit after the decimal point
 */
const trunc1 = value => {
  return truncBy(value, 10);
};
/**
 * Truncates passed value with two digits after the decimal point
 */
const trunc2 = value => {
  return truncBy(value, 100);
};
/**
 * Truncates passed value with three digits after the decimal point
 */
const trunc3 = value => {
  return truncBy(value, 1000);
};
/**
 * Truncates passed value with four digits after the decimal point
 */
const trunc4 = value => {
  return truncBy(value, 10000);
};
/**
 * Truncates passed value with five digits after the decimal point
 */
const trunc5 = value => {
  return truncBy(value, 100000);
};
/**
 * Truncates passed value with ten digits after the decimal point
 */
const trunc10 = value => {
  return truncBy(value, 10000000000);
};

/**
 * Rounds the value down after the given decimal.
 * Used the rounding method of y(x, m) = (x * m) / m
 * This is a fast implementation when the magnitude is known
 *
 * @param value Value to ceil
 * @param magnitude The power of 10 used to achieve certain precision
 */
const floorBy = (value, magnitude = 1) => {
  return Math.floor((value + Number.EPSILON) * magnitude) / magnitude;
};

/**
 * Rounds the value down after the given decimal.
 * This is a slower implementation than `floorBy` and all its variants (floor1, floor2...)
 *
 * @param value Value to floor
 * @param precision The amount of decimals to achieve
 */
const floor = (value, precision = 0) => {
  return floorBy(value, 10 ** precision);
};
/**
 * Rounds the value down after the first decimal.
 */
const floor1 = value => {
  return floorBy(value, 10);
};
/**
 * Rounds the value down after the second decimal.
 */
const floor2 = value => {
  return floorBy(value, 100);
};
/**
 * Rounds the value down after the third decimal.
 */
const floor3 = value => {
  return floorBy(value, 1000);
};
/**
 * Rounds the value down after the fourth decimal.
 */
const floor4 = value => {
  return floorBy(value, 10000);
};

/* ------------------------------------------------------------------------------------
 * Constant values
 * ------------------------------------------------------------------------------------
 */
/**
 * gravitational acceleration
 *
 *  HM: Calculations always use a gravitational acceleration of 9,81 m/s^2
 */
const GRAVITATIONAL_ACCELERATION = 9.81; // m/s^2
/* ------------------------------------------------------------------------------------
 * Enums
 * ------------------------------------------------------------------------------------
 */
/**
 * UnitSystem can be metric or imperial
 */
var UnitSystem;
(function (UnitSystem) {
  UnitSystem[UnitSystem["metric"] = 1] = "metric";
  UnitSystem[UnitSystem["imperial"] = 2] = "imperial";
})(UnitSystem || (UnitSystem = {}));

/**
 * basic domain model
 *
 */
class Domain {
  /**
   * Constructor
   *
   * @param logger            a kopla_domain [[logger]] Object
   */
  constructor(logger) {
    this._logger = void 0;
    /**
     * The coutryCode has to be in 2ALPHA format: [A-Z][A-Z].
     * See https://en.wikipedia.org/wiki/ISO_3166-1 for details.
     *
     * The default value is 'DE'.
     */
    this._countryCode = 'DE';
    /**
     * The default unit system is 'metric'.
     */
    this._unitSystem = UnitSystem.metric;
    /**
     * The child module version.
     */
    this._moduleVersion = void 0;
    /**
     * The dataMigration model.
     */
    this._dataMigration = void 0;
    /**
     * The dataSchemaChecks object contains key value pairs of properties which
     * contain data-objects
     * with schemaVersion property
     */
    this._dataSchemaChecks = void 0;
    this._logger = logger;
  }
  /**
   * Retrieves `this` as deserializable string.
   *
   * @returns The deserializable string
   */
  serialize() {
    const props = {};
    let key;
    for (key in this) {
      if (key !== '_logger' && key !== '_dataMigration' && key !== '_dataSchemaChecks') {
        props[key] = this[key];
      }
    }
    return JSON.stringify(props);
  }
  /**
   * Deserializes a given string into `this`.
   *
   * @param serializedObject    The formerly serialized object to deserialize, but it can also be
   *                            a configuration to crate an object
   */
  deserialize(serializedObject, logger) {
    if (typeof logger === 'undefined') {
      throw new Error('parameter logger is missing or undefined');
    }
    let props;
    // the serialized object (string) can contain different representations of the object
    // * the serialized model [pass through]
    // * a json configuration [proccesing not included in kopla_domain]
    // * a xml configuration  [proccesing not included in kopla_domain]
    // overload this method in your domain to process the different types
    serializedObject = this.preProcessSerializedObject(serializedObject);
    // If the passed string could not be determined preProcessSerializedObject returns empty
    // curly braces. (e.g. if you pass a png or pdf)
    if (serializedObject === '{}') {
      return new ValidationResult(2, 1, 'Could not serialize object. No properties set.');
    }
    // migrate model
    if (this._dataMigration) {
      const dc = new DataConsistency(logger, serializedObject, this._moduleVersion);
      if (!dc.isLatestVersion()) {
        try {
          props = dc.migrate(this._dataMigration);
        } catch (e) {
          this._logger.error('Model.deserialize: Could not migrate following model to latest version: ' + serializedObject, e);
          return new ValidationResult(2, 1, 'Could not migrate from ' + this._moduleVersion + ' to latest version');
        }
      }
    }
    // check if data version is compatible to current model
    if (this._dataSchemaChecks) {
      const vResult = this.doDataSchemaCheck(this._dataSchemaChecks);
      if (vResult.type === 2) {
        return vResult;
      }
    }
    try {
      props != null ? props : props = JSON.parse(serializedObject);
      props = this.cleanProperties(props);
    } catch (_unused) {
      this._logger.error('Model.deserialize: Cannot parse serialized Object as JSON: ' + serializedObject);
      return new ValidationResult(2, 1, 'Could not parse serialized Object as JSON');
    }
    this._logger = logger;
    let validation = this.checkProperties(props);
    validation != null ? validation : validation = this.setProperties(props);
    return validation;
  }
  /**
   * Sets _moduleVersion to version from child package.json
   */
  setModuleVersion(version) {
    this._moduleVersion = version;
  }
  /**
   * Returns _moduleVersion from child package.json
   */
  getModuleVersion() {
    return this._moduleVersion;
  }
  /**
   * Sets dataMigration model
   */
  setDataMigration(data) {
    this._dataMigration = data;
  }
  /**
   * Sets dataProperty with allowed schemaVersion
   * The model will be checked if dataProperty contains only data-objects with
   * given schemaVersion
   * to prevent incompatible status
   */
  setDataSchemaVersion(dataProperty, schemaVersion) {
    var _this$_dataSchemaChec;
    (_this$_dataSchemaChec = this._dataSchemaChecks) != null ? _this$_dataSchemaChec : this._dataSchemaChecks = {};
    this._dataSchemaChecks[dataProperty] = schemaVersion;
  }
  /**
   * Sets the unit system.
   *
   * The default value is metric.
   */
  setUnitSystem(unitSystem) {
    this._unitSystem = unitSystem;
  }
  /**
   * Returns the current unit system.
   */
  getUnitSystem() {
    return this._unitSystem;
  }
  /**
   * Returns true, if the unit system is metric.
   */
  isUnitSystemMetric() {
    return this._unitSystem === UnitSystem.metric;
  }
  /**
   * Returns true, if the unit system is imperial.
   */
  isUnitSystemImperial() {
    return this._unitSystem === UnitSystem.imperial;
  }
  /**
   * Sets the countryCode.
   */
  setCountryCode(countryCode) {
    if (!/^[A-Z][A-Z]$/.exec(countryCode)) {
      throw new Error('countryCode has to be in 2ALPHA format');
    }
    this._countryCode = countryCode;
  }
  /**
   * Returns the 2APLHA countryCode
   */
  getCountryCode() {
    return this._countryCode;
  }
  /**
   * This method is intended to be overloaded by the inheriting domain.
   *
   * it is called while deserialization and can be used to deserialize from different formats
   */
  preProcessSerializedObject(str) {
    let props = {};
    const appPattern = this.getAppNamePattern();
    if (str.match(appPattern)) {
      props = this.getPropertiesFromConfig(str);
      if (props === '{}') {
        return '{}';
      }
    } else if (/_countryCode/.exec(str)) {
      try {
        props = JSON.parse(str);
      } catch (_unused2) {
        return '{}';
      }
    } else {
      console.log('The passed input seems not to be a valid configuration! I is ignored!');
      return '{}';
    }
    return JSON.stringify(props);
  }
  /**
   * Returns the name of the application which is using this domain.
   *
   * This method is intended to be overloaded by the inheriting domain.
   *
   * It is internally used to identify configurations!
   */
  getAppName() {
    return 'NAME-OF-THE-APPLICATION';
  }
  /**
   * This method returns the pattern for finding the app-name in confingurations.
   */
  getAppNamePattern() {
    const appName = this.getAppName();
    return RegExp('[\'"]' + appName + '[\'"]');
  }
  /**
   * This method turns a configuration into an object containing all properties
   * and returns it.
   *
   * This method is called before the migration so old/unknown fields may be in the configuration
   * and we cannot remove them.
   *
   * To be sure that no invalid fields are in the configuration and no harmful content is inside
   * the method cleanProperties() is called after migrating the properties in deserialize().
   *
   * So we do not care for security here!
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getPropertiesFromConfig(config) {
    let props = {};
    // convert ' to " global
    config = config.replace(/'/g, '"');
    // console.log(config);
    try {
      props = JSON.parse(config);
    } catch (_unused3) {
      // throw new Error('Cannot parse configuration! ' + ex);
      return '{}';
    }
    if (props.app === this.getAppName() && props.model) {
      props = props.model;
    }
    return props;
  }
  /**
   * This method is intended to be overloaded by the inheriting domain.
   *
   * it is called while deserialization and can be used to remove unwanted fields in the properties
   * after migration
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cleanProperties(props) {
    return props;
  }
  /**
   * This method is intended to be overloaded by the inheriting domain.
   *
   * it is called while deserialization and can be used to check if the properties are valid
   * after migration
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  checkProperties(_) {
    return null;
  }
  /**
   * This method can be overloaded by the inheriting domain.
   * This method copies the relevant properties th the model and returns a ValidationResult.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setProperties(props) {
    let vResult = new ValidationResult(0, 1, '');
    let key;
    for (key in props) {
      if (key in this) {
        this[key] = props[key];
      } else {
        this._logger.warn('Model.deserialize: Cannot set unknown property key: ' + key);
        vResult = new ValidationResult(1, 1, 'Could not parse serialized Object as JSON');
      }
    }
    return vResult;
  }
  doDataSchemaCheck(dataSchemaChecks) {
    this._logger.log('Run data schema checks', dataSchemaChecks);
    let vResult = new ValidationResult(0, 1, '');
    for (const prop of Object.keys(dataSchemaChecks)) {
      const checkSchemaVersion = dataSchemaChecks[prop];
      this._logger.log('Check of property ' + prop + ' contains data with schemaVersion ' + checkSchemaVersion);
      if (prop in this) {
        const data = this[prop];
        this._logger.log('Identified data to check', data);
        // data has to be an array of objects or an object with property
        // schemaVersion
        if (data instanceof Array) {
          // this._logger.log('ARRAY');
          for (const dataRecord of data) {
            vResult = this.validateDataRecord(dataRecord, prop, checkSchemaVersion);
            if (vResult.type === 2) {
              return vResult;
            }
          }
        } else if (typeof data === 'object') {
          vResult = this.validateDataRecord(data, prop, checkSchemaVersion);
          if (vResult.type === 2) {
            return vResult;
          }
        } else {
          this._logger.error('Data check failed for property ' + prop + ' including data: ', data);
          vResult = new ValidationResult(2, 2, 'Data check failed for property ' + prop);
          return vResult;
        }
      }
    }
    return vResult;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validateDataRecord(record, schemaProp, checkSchemaVersion) {
    let vResult = new ValidationResult(0, 1, '');
    const resultAsSchema = record;
    if (!resultAsSchema.schemaVersion) {
      this._logger.error('DataRecord of model-property ' + schemaProp + ' has no schemaVersion property');
      vResult = new ValidationResult(2, 2, 'DataRecord of model - property ' + schemaProp + ' has no schemaVersion property');
      return vResult;
    }
    if (resultAsSchema.schemaVersion !== checkSchemaVersion) {
      const error = `DataRecord of model-property ${schemaProp} (${resultAsSchema.schemaVersion}) does not equal schemaVersion ${checkSchemaVersion}`;
      this._logger.error(error);
      vResult = new ValidationResult(2, 2, error);
      return vResult;
    } else {
      this._logger.log(`DataRecord of model-property ${schemaProp} equals schemaVersion ${checkSchemaVersion}. Nothing to do`);
    }
    return vResult;
  }
}

/**
 * The names of the headers used to define the session
 */
const salesModeSessionHeaders = {
  id: 'kopla-session-id',
  server: 'kopla-session-server'
};

/**
 * Type guard helper function to validate the value being a MailToCustomerCareRequestModel
 */
const isMailToCustomerCareRequestModel = whatever => {
  return !!whatever && typeof whatever === 'object' && !!whatever.mailSubject &&
  // Because we use this type guard function to validate incoming objects, we still want to check if appId is provided.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  !!whatever.appId && !!whatever.countryCode && !!whatever.languageCode && !!whatever.mailSubject && !!whatever.mailText;
};

/**
 * The request modes supported by the ipro svc api.
 * Remark: Modes that are supported by the iprosvc api but are currently not used in kopla and
 * are thus not supported yet by kopla are commented out.
 */
var IproSvcAppServiceRequestMode;
(function (IproSvcAppServiceRequestMode) {
  // The following modes are supported by the iprosvc api, but are currently not used.
  // AssemblyCostRequest = 'montagekostenAnfrage',
  // AssemblyCostHelpContact = 'montagekostenHilfeKontakt',
  // drylinlineartischewunschkonfiguratorkunde = 'drylinlineartischewunschkonfiguratorkunde', // similar to servertoflexpdf
  // drylinlineartischewunschkonfiguratorfertigung = 'drylinlineartischewunschkonfiguratorfertigung' // similar to servertoflexpdf
  /**
   * Sends an email to igus customer care. The email recipient is deducted from app id and locale.
   */
  IproSvcAppServiceRequestMode["request"] = "request";
  /**
   * Sends an email to an igus customer (e.g. any recipient) with a PDF file attached.
   * The mail text is generated from app id and locale.
   */
  IproSvcAppServiceRequestMode["clientpdf"] = "clientpdf";
  /**
   * Sends an email to an igus customer (e.g. any recipient). The mail text is generated from app id and locale.
   */
  IproSvcAppServiceRequestMode["clientconfig"] = "clientconfig";
  /**
   * Generates a PDF file, based on the data specified in the ServerPDF field and sends it to any recipient.
   */
  IproSvcAppServiceRequestMode["serverpdf"] = "serverpdf";
  /**
   * Generates a PDF, based on the data specified in the ServerPDF field, depending on specified app id.
   * Return three fields: "result:1;pdfData:[base64 encoded pdf data];pdfName:[specified file name]"
   */
  IproSvcAppServiceRequestMode["servertoflexpdf"] = "servertoflexpdf";
})(IproSvcAppServiceRequestMode || (IproSvcAppServiceRequestMode = {}));
/**
 * Returns whether the given `mode` would send an email directly to the customer
 */
const iproSvcRequestModeSendsDirectlyToCustomer = mode => {
  return [IproSvcAppServiceRequestMode.clientconfig, IproSvcAppServiceRequestMode.clientpdf, IproSvcAppServiceRequestMode.serverpdf].includes(mode);
};

/**
 * Defines delivery time level
 *
 * More info: https://igusdev.atlassian.net/wiki/spaces/KIIGUS/pages/2125562185/Delivery+time
 */
var DeliveryTimeLevel;
(function (DeliveryTimeLevel) {
  DeliveryTimeLevel[DeliveryTimeLevel["onRequest"] = 0] = "onRequest";
  DeliveryTimeLevel[DeliveryTimeLevel["slowAvailability"] = 1] = "slowAvailability";
  DeliveryTimeLevel[DeliveryTimeLevel["fastAvailability"] = 2] = "fastAvailability";
  DeliveryTimeLevel[DeliveryTimeLevel["fastlane"] = 3] = "fastlane";
})(DeliveryTimeLevel || (DeliveryTimeLevel = {}));

/**
 * The `QuantityAccessor` is a stateful utility class to help working with quantities.
 *
 * It maintains a ref to a `Quantity` object to easily read and write on it.
 * One of the main goals is to perform calculation of unit-based values.
 *
 * Examples:
 *
 * - Length.meter(3)             gets a `QuantityAccessor` with a meter value of 3
 * - Length.from(x)              gets a `QuantityAccessor` with a value/unit pair defined by x
 * - Length.from(x).meter        gets the meter value of x
 * - Length.from(x).inch         gets the inch value of x
 * - length(x).meter             gets the meter value of x (just a shortcut for the example above)
 * - length(x).millimeter = 7    sets x to 7mm
 * - Length.meter(3).get()       gets a `Quantity` with 3m
 */
class QuantityAccessor {
  constructor(quantity) {
    this.quantity = void 0;
    this.quantity = quantity;
  }
  /**
   * Retrieves a `Quantity` object.
   */
  get() {
    return this.quantity;
  }
}

var AccelerationUnit;
(function (AccelerationUnit) {
  AccelerationUnit["meterPerSquareSecond"] = "m_s2";
})(AccelerationUnit || (AccelerationUnit = {}));
class AccelerationAccessor extends QuantityAccessor {
  // factory methods
  static from(q) {
    return new AccelerationAccessor(q);
  }
  static meterPerSquareSecond(v) {
    return new AccelerationAccessor({
      value: v,
      unit: AccelerationUnit.meterPerSquareSecond
    });
  }
  // setters
  set meterPerSquareSecond(v) {
    this.quantity.value = v;
    this.quantity.unit = AccelerationUnit.meterPerSquareSecond;
  }
  // getters
  get meterPerSquareSecond() {
    return this.quantity.value;
  }
}
const acceleration = q => {
  return AccelerationAccessor.from(q);
};

var AngleUnit;
(function (AngleUnit) {
  AngleUnit["degree"] = "deg";
})(AngleUnit || (AngleUnit = {}));
class AngleAccessor extends QuantityAccessor {
  // factory methods
  static from(q) {
    return new AngleAccessor(q);
  }
  static deg(v) {
    return new AngleAccessor({
      value: v,
      unit: AngleUnit.degree
    });
  }
  // setters
  set deg(v) {
    this.quantity.value = v;
    this.quantity.unit = AngleUnit.degree;
  }
  // getters
  get deg() {
    return this.quantity.value;
  }
}
const angle = q => {
  return AngleAccessor.from(q);
};

var AnglePerLengthUnit;
(function (AnglePerLengthUnit) {
  AnglePerLengthUnit["degreePerMeter"] = "deg_m";
})(AnglePerLengthUnit || (AnglePerLengthUnit = {}));
class AnglePerLengthAccessor extends QuantityAccessor {
  // factory methods
  static from(q) {
    return new AnglePerLengthAccessor(q);
  }
  static degreePerMeter(v) {
    return new AnglePerLengthAccessor({
      value: v,
      unit: AnglePerLengthUnit.degreePerMeter
    });
  }
  // setters
  set degreePerMeter(v) {
    this.quantity.value = v;
    this.quantity.unit = AnglePerLengthUnit.degreePerMeter;
  }
  // getters
  get degreePerMeter() {
    return this.quantity.value;
  }
}
const anglePerLength = q => {
  return AnglePerLengthAccessor.from(q);
};

var AngularAccelerationUnit;
(function (AngularAccelerationUnit) {
  AngularAccelerationUnit["degreePerSquareSecond"] = "deg_s2";
})(AngularAccelerationUnit || (AngularAccelerationUnit = {}));
class AngularAccelerationAccessor extends QuantityAccessor {
  // factory methods
  static from(q) {
    return new AngularAccelerationAccessor(q);
  }
  static degreePerSquareSecond(v) {
    return new AngularAccelerationAccessor({
      value: v,
      unit: AngularAccelerationUnit.degreePerSquareSecond
    });
  }
  // setters
  set degreePerSquareSecond(v) {
    this.quantity.value = v;
    this.quantity.unit = AngularAccelerationUnit.degreePerSquareSecond;
  }
  // getters
  get degreePerSquareSecond() {
    return this.quantity.value;
  }
}
const angularAcceleration = q => {
  return AngularAccelerationAccessor.from(q);
};

var ForceUnit;
(function (ForceUnit) {
  ForceUnit["kiloNewton"] = "kN";
  ForceUnit["newton"] = "N";
  ForceUnit["dekaNewton"] = "daN";
  ForceUnit["centiNewton"] = "cN";
  ForceUnit["milliNewton"] = "mN";
})(ForceUnit || (ForceUnit = {}));
class ForceAccessor extends QuantityAccessor {
  // factory methods
  static from(q) {
    return new ForceAccessor(q);
  }
  static newton(v) {
    return new ForceAccessor({
      value: v,
      unit: ForceUnit.newton
    });
  }
  static dekaNewton(v) {
    return new ForceAccessor({
      value: v,
      unit: ForceUnit.dekaNewton
    });
  }
  static kiloNewton(v) {
    return new ForceAccessor({
      value: v,
      unit: ForceUnit.kiloNewton
    });
  }
  static centiNewton(v) {
    return new ForceAccessor({
      value: v,
      unit: ForceUnit.centiNewton
    });
  }
  static milliNewton(v) {
    return new ForceAccessor({
      value: v,
      unit: ForceUnit.milliNewton
    });
  }
  // setters / getters
  set newton(v) {
    this.quantity.value = v;
    this.quantity.unit = ForceUnit.newton;
  }
  get newton() {
    switch (this.quantity.unit) {
      case ForceUnit.kiloNewton:
        return this.quantity.value * 1000;
      case ForceUnit.dekaNewton:
        return this.quantity.value * 10;
      case ForceUnit.centiNewton:
        return this.quantity.value / 100;
      case ForceUnit.milliNewton:
        return this.quantity.value / 1000;
      default:
      case ForceUnit.newton:
        return this.quantity.value;
    }
  }
  set kiloNewton(v) {
    this.quantity.value = v;
    this.quantity.unit = ForceUnit.kiloNewton;
  }
  get kiloNewton() {
    return this.newton / 1000;
  }
  set dekaNewton(v) {
    this.quantity.value = v;
    this.quantity.unit = ForceUnit.dekaNewton;
  }
  get dekaNewton() {
    return this.newton / 10;
  }
  set centiNewton(v) {
    this.quantity.value = v;
    this.quantity.unit = ForceUnit.centiNewton;
  }
  get centiNewton() {
    return this.newton * 100;
  }
  set milliNewton(v) {
    this.quantity.value = v;
    this.quantity.unit = ForceUnit.milliNewton;
  }
  get milliNewton() {
    return this.newton * 1000;
  }
}
const force = q => {
  return ForceAccessor.from(q);
};

var FrequencyUnit;
(function (FrequencyUnit) {
  FrequencyUnit["hertz"] = "hz";
  FrequencyUnit["perSecond"] = "_s";
  FrequencyUnit["perHour"] = "_h";
})(FrequencyUnit || (FrequencyUnit = {}));
class FrequencyAccessor extends QuantityAccessor {
  // factory methods
  static from(q) {
    return new FrequencyAccessor(q);
  }
  static hertz(v) {
    return new FrequencyAccessor({
      value: v,
      unit: FrequencyUnit.hertz
    });
  }
  static perSecond(v) {
    return new FrequencyAccessor({
      value: v,
      unit: FrequencyUnit.perSecond
    });
  }
  static perHour(v) {
    return new FrequencyAccessor({
      value: v,
      unit: FrequencyUnit.perHour
    });
  }
  set hertz(v) {
    this.quantity.value = +v;
    this.quantity.unit = FrequencyUnit.hertz;
  }
  get hertz() {
    switch (this.quantity.unit) {
      case FrequencyUnit.perSecond:
        return this.quantity.value;
      case FrequencyUnit.perHour:
        return this.quantity.value / 3600;
      default:
      case FrequencyUnit.hertz:
        return this.quantity.value;
    }
  }
  set perSecond(v) {
    this.quantity.value = +v;
    this.quantity.unit = FrequencyUnit.perSecond;
  }
  get perSecond() {
    return this.quantity.unit === FrequencyUnit.perSecond ? this.quantity.value : this.hertz;
  }
  set perHour(v) {
    this.quantity.value = +v;
    this.quantity.unit = FrequencyUnit.perHour;
  }
  get perHour() {
    return this.quantity.unit === FrequencyUnit.perHour ? this.quantity.value : this.hertz * 3600;
  }
}
const frequency = q => {
  return FrequencyAccessor.from(q);
};

/* ------------------------------------------------------------------------------------
 * Constant values
 * ------------------------------------------------------------------------------------
 */
/**
 * multiply force in N by FACTOR_FORCE to get the value in pound-force lb (lbf)
 *
 *   FORCE F
 *     metric              imperial
 *
 *     N  (kg m/s^2)   ->  lb (lbf / Pund force)
 *     1 N             =   0,224809 Pound-force
 *
 *     F[N] * FACTOR_FORCE = F[lb]
 */
const FACTOR_FORCE = 0.224809;
/**
 * multiply force in kN by FACTOR_FORCE_KILO to get the value in kipf kilo pound-force
 *
 *   FORCE F
 *     metric              imperial
 *
 *     kN              ->  kipf
 *     1 kN            =   0,224809 kipf
 *
 *     F[kN] * FACTOR_FORCE_KILO = F[kipf]
 */
const FACTOR_FORCE_KILO = 0.224809;
/**
 * multiply mikro distance in µm  by FACTOR_DISTANCE_MIKRO to get the value in
 * mil
 *
 *   DISTANCE l
 *     metric              imperial
 *
 *     µm              ->  mil
 *     1 µm            =   0.03937 mil
 *
 *     l[µm] * FACTOR_DISTANCE_MIKRO = l[mil]
 */
const FACTOR_DISTANCE_MIKRO = 0.03937007;
/**
 * multiply milli distance in mm  by FACTOR_DISTANCE_MILLI to get the value in
 * in
 *
 *   DISTANCE l
 *     metric              imperial
 *
 *     mm              ->  in
 *     1 mm            =   0.03937 in
 *
 *     l[mm] * FACTOR_DISTANCE_MILLI = l[in]
 */
const FACTOR_DISTANCE_MILLI = 0.03937007;
/**
 * multiply distance in m by FACTOR_DISTANCE to get the value in ft
 *
 *   DISTANCE l
 *     metric              imperial
 *
 *     m               ->  ft
 *     1 m             =   3.28084 ft
 *
 *     l[m] * FACTOR_DISTANCE = l[ft]
 */
const FACTOR_DISTANCE = 3.28084;
/**
 * multiply kilo distance in km by FACTOR_DISTANCE_KILO to get the value in
 * miles
 *
 *   DISTANCE l
 *     metric              imperial
 *
 *     km              ->  miles
 *     1 km            =   0.621373 miles
 *
 *     l[km] * FACTOR_DISTANCE_KILO = l[miles]
 */
const FACTOR_DISTANCE_KILO = 0.621373;
/**
 * multiply mass in g by FACTOR_MASS to get the value in oz
 *
 *   MASS m
 *     metric              imperial
 *
 *     g              ->  oz
 *     1 g            =   0.032151 oz
 *
 *     m[g] * FACTOR_MASS = m[oz]
 */
const FACTOR_MASS = 0.032151;
/**
 * multiply kilo mass in kg by FACTOR_MASS_KILO to get the value in lbm
 *
 *   MASS m
 *     metric              imperial
 *
 *     kg              ->  lbm
 *     1 kg            =   2.204623 lbm
 *
 *     m[kg] * FACTOR_MASS_KILO = m[lbm]
 */
const FACTOR_MASS_KILO = 2.204623;
/**
 * multiply SMOA in m^4 by FACTOR_SMOA to get the value in ft^4
 *
 * https://en.wikipedia.org/wiki/Second_moment_of_area
 *
 *   SMOA I
 *     metric              imperial
 *
 *     m^4             ->  ft^4
 *     1 m^4           =   115.861767 ft^4
 *
 *     I[m^4] * FACTOR_SMOA = I[ft^4]
 */
const FACTOR_SMOA = 115.861767;
/**
 * multiply Milli SMOA in mm^4 by FACTOR_SMOA_MILLI to get the value in in^4
 *
 * https://en.wikipedia.org/wiki/Second_moment_of_area
 *
 *   SMOA I
 *     metric              imperial
 *
 *     mm^4            ->  in^4
 *     1 mm^4           =  0.0000024025 in^4
 *
 *     I[mm^4] * FACTOR_SMOA_MILLI = I[in^4]
 */
const FACTOR_SMOA_MILLI = 0.0000024025;
/**
 * multiply Pressure in Pa by FACTOR_PRESSURE to get the value in psi
 *
 *   Pressure p
 *     metric              imperial
 *
 *     Pa              ->  psi
 *     1 Pa            =   0.000145038 psi
 *
 *     p[Pa] * FACTOR_PRESSURE = p[psi]
 */
const FACTOR_PRESSURE = 0.000145038;
/**
 * multiply Mega Pressure in MPa by FACTOR_PRESSURE_MEGA to get the value in psi
 *
 *   Pressure p
 *     metric              imperial
 *
 *     MPa              ->  psi
 *     1 MPa            =  145.038 psi
 *
 *     p[MPa] * FACTOR_PRESSURE_MEGA = p[psi]
 */
const FACTOR_PRESSURE_MEGA = 145.038;
/**
 * multiply velocity v in m/s by FACTOR_VELOCITY to get the value in ft/s
 *
 *   VELOCITY v
 *     metric              imperial
 *
 *     m/s             ->  ft/s
 *     1               =   3.28084 ft/s
 *
 *     v[m/s] * FACTOR_VELOCITY = v[ft/min]
 */
const FACTOR_VELOCITY = 3.28084;
/**
 * multiply velocity (/minute) v in m/min by FACTOR_VELOCITY_MINUTE to get
 * the value in ft/min
 *
 *   VELOCITY v
 *     metric              imperial
 *
 *     m/min           ->  ft/min
 *     1 m/min         =   3.28084 ft/min
 *
 *     v[m/min] * FACTOR_VELOCITY_MINUTE = v[ft/min]
 */
const FACTOR_VELOCITY_MINUTE = 3.28084;
/**
 * multiply volume V in m^3 by FACTOR_VOLUME to get the value in ft^3
 *
 *   VOLUME V
 *     metric              imperial
 *
 *     m^3             ->  ft^3
 *     1 m^3           =   35.3147 ft^3
 *
 *     V[m^3] * FACTOR_VOLUME = V[ft^3]
 */
const FACTOR_VOLUME = 35.3147;
/**
 * multiply volume V in mm^3 by FACTOR_VOLUME_MILLI to get the value in in^3
 *
 *   VOLUME V
 *     metric              imperial
 *
 *     mm^3             ->  in^3
 *     1 mm^3           =   0.0000610237 in^3
 *
 *     V[mm^3] * FACTOR_VOLUME_MILLI = V[in^3]
 */
const FACTOR_VOLUME_MILLI = 0.0000610237;
/**
 * multiply acceleration in m/s^2 by FACTOR_ACCELERATION to get the value in
 * ft/s^2
 *
 *   ACCELERATION a
 *     metric              imperial
 *
 *     m/s^2           ->  ft/s^2 (foot per square second)
 *     1               =   3.28084 ft/s^2
 *
 *     a[m/s^2] * FACTOR_ACCELERATION = a[ft/s^2]
 */
const FACTOR_ACCELERATION = 3.28084;
/**
 * temperature calculation
 *
 */
const FACTOR_TEMPERATURE = 5 / 9;
const CONST_TEMPERATURE = 32;
/**
 * multiply milli area A (mm^2) by FACTOR_AREA to get the value in in^2
 *
 *   ACCELERATION a
 *     metric              imperial
 *
 *     mm^2            ->  in^2
 *     1               =   0.0015499 in/s^2
 *
 *     A[mm^2] * FACTOR_AREA_MILLI = A[in^2]
 */
const FACTOR_AREA_MILLI = 0.0015499;
/**
 * multiply area A (m^2) by FACTOR_AREA to get the value in ft^2
 *
 *   ACCELERATION a
 *     metric              imperial
 *
 *     m^2             ->  ft^2
 *     1               =   10.7639 ft/s^2
 *
 *     A[m^2] * FACTOR_AREA = A[ft^2]
 */
const FACTOR_AREA = 10.7639;
/**
 * multiply kilo area A (km^2) by FACTOR_AREA_KILO to get the value in miles^2
 *
 *   Area A
 *     metric              imperial
 *
 *     km^2            ->  miles^2
 *     1               =   0.38610 miles/s^2
 *
 *     A[km^2] * FACTOR_AREA_KILO = A[miles^2]
 */
const FACTOR_AREA_KILO = 0.3861;
/**
 * multiply torque M (Nm) by FACTOR_TORQUE to get the value in lbf*ft
 *
 *   TORQUE M
 *     metric              imperial
 *
 *     M               ->  lbf*ft
 *     1 Nm            =   0.737562 lbf*ft
 *
 *     M[Nm] * FACTOR_TORQUE = M[lbf*ft]
 */
const FACTOR_TORQUE = 0.737562;

var LengthUnit;
(function (LengthUnit) {
  LengthUnit["kilometer"] = "km";
  LengthUnit["meter"] = "m";
  LengthUnit["centimeter"] = "cm";
  LengthUnit["millimeter"] = "mm";
  LengthUnit["micrometer"] = "um";
  LengthUnit["mile"] = "mi";
  LengthUnit["inch"] = "inch";
  LengthUnit["feet"] = "ft";
})(LengthUnit || (LengthUnit = {}));
class LengthAccessor extends QuantityAccessor {
  // factory methods
  static from(q) {
    return new LengthAccessor(q);
  }
  static kilometer(v) {
    return new LengthAccessor({
      value: v,
      unit: LengthUnit.kilometer
    });
  }
  static meter(v) {
    return new LengthAccessor({
      value: v,
      unit: LengthUnit.meter
    });
  }
  static centimeter(v) {
    return new LengthAccessor({
      value: v,
      unit: LengthUnit.centimeter
    });
  }
  static millimeter(v) {
    return new LengthAccessor({
      value: v,
      unit: LengthUnit.millimeter
    });
  }
  static micrometer(v) {
    return new LengthAccessor({
      value: v,
      unit: LengthUnit.micrometer
    });
  }
  static mile(v) {
    return new LengthAccessor({
      value: v,
      unit: LengthUnit.mile
    });
  }
  static feet(v) {
    return new LengthAccessor({
      value: v,
      unit: LengthUnit.feet
    });
  }
  static inch(v) {
    return new LengthAccessor({
      value: v,
      unit: LengthUnit.inch
    });
  }
  set kilometer(v) {
    this.quantity.value = +v;
    this.quantity.unit = LengthUnit.kilometer;
  }
  get kilometer() {
    return this.meter / 1000;
  }
  set meter(v) {
    this.quantity.value = +v;
    this.quantity.unit = LengthUnit.meter;
  }
  get meter() {
    switch (this.quantity.unit) {
      case LengthUnit.kilometer:
        return this.quantity.value * 1000;
      case LengthUnit.meter:
        return this.quantity.value;
      case LengthUnit.centimeter:
        return this.quantity.value / 100;
      case LengthUnit.micrometer:
        return this.quantity.value / 1000000;
      case LengthUnit.mile:
        return this.quantity.value / FACTOR_DISTANCE_KILO * 1000;
      case LengthUnit.feet:
        return this.quantity.value / FACTOR_DISTANCE;
      case LengthUnit.inch:
        return this.quantity.value / FACTOR_DISTANCE_MILLI / 100;
      default:
      case LengthUnit.millimeter:
        return this.quantity.value / 1000;
    }
  }
  set centimeter(v) {
    this.quantity.value = +v;
    this.quantity.unit = LengthUnit.centimeter;
  }
  get centimeter() {
    return this.quantity.unit === LengthUnit.centimeter ? this.quantity.value : this.meter * 100;
  }
  set millimeter(v) {
    this.quantity.value = +v;
    this.quantity.unit = LengthUnit.millimeter;
  }
  get millimeter() {
    return this.quantity.unit === LengthUnit.millimeter ? this.quantity.value : this.meter * 1000;
  }
  set micrometer(v) {
    this.quantity.value = +v;
    this.quantity.unit = LengthUnit.micrometer;
  }
  get micrometer() {
    return this.quantity.unit === LengthUnit.micrometer ? this.quantity.value : this.meter * 1000000;
  }
  set mile(v) {
    this.quantity.value = +v;
    this.quantity.unit = LengthUnit.mile;
  }
  get mile() {
    return this.quantity.unit === LengthUnit.mile ? this.quantity.value : this.kilometer * FACTOR_DISTANCE_KILO;
  }
  set feet(v) {
    this.quantity.value = +v;
    this.quantity.unit = LengthUnit.feet;
  }
  get feet() {
    return this.quantity.unit === LengthUnit.feet ? this.quantity.value : this.meter * FACTOR_DISTANCE;
  }
  set inch(v) {
    this.quantity.value = +v;
    this.quantity.unit = LengthUnit.inch;
  }
  get inch() {
    return this.quantity.unit === LengthUnit.inch ? this.quantity.value : this.meter * FACTOR_DISTANCE_MILLI;
  }
}
const length = q => {
  return LengthAccessor.from(q);
};

var TemperatureUnit;
(function (TemperatureUnit) {
  TemperatureUnit["celsius"] = "\u00B0C";
  TemperatureUnit["fahrenheit"] = "\u00B0F";
  TemperatureUnit["kelvin"] = "K";
})(TemperatureUnit || (TemperatureUnit = {}));
class TemperatureAccessor extends QuantityAccessor {
  static from(q) {
    return new TemperatureAccessor(q);
  }
  static celsius(v) {
    return new TemperatureAccessor({
      value: v,
      unit: TemperatureUnit.celsius
    });
  }
  static fahrenheit(v) {
    return new TemperatureAccessor({
      value: v,
      unit: TemperatureUnit.fahrenheit
    });
  }
  static kelvin(v) {
    return new TemperatureAccessor({
      value: v,
      unit: TemperatureUnit.kelvin
    });
  }
  static areEqual(a, b) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!a && b || a && !b) return false;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!a && !b) return true;
    return a.celsius === b.celsius;
  }
  /**
   * Returns the lower of the two given temperatures whereas `undefined` is not considered to be
   * lower. I.e. if `a` is `undefined`, `b` will be returned. If both are `undefined`, `undefined`
   * will be returned.
   */
  static getLower(a, b) {
    if (a && b) {
      if (a.celsius < b.celsius) {
        return a;
      } else {
        return b;
      }
    }
    if (a) {
      return a;
    } else {
      return b;
    }
  }
  /**
   * Returns the higher of the two given temperatures whereas `undefined` is not considered to be
   * lower. Acts like `getLower`.
   */
  static getHigher(a, b) {
    if (a && b) {
      if (a.celsius > b.celsius) {
        return a;
      } else {
        return b;
      }
    }
    if (a) {
      return a;
    } else {
      return b;
    }
  }
  set celsius(v) {
    this.quantity.value = v;
    this.quantity.unit = TemperatureUnit.celsius;
  }
  get celsius() {
    switch (this.quantity.unit) {
      case TemperatureUnit.fahrenheit:
        return (this.quantity.value - 32) / 1.8;
      case TemperatureUnit.kelvin:
        return this.quantity.value - 273.15;
      case TemperatureUnit.celsius:
        return this.quantity.value;
      default:
        throw new Error('Temperature.celsius: Unit not supported');
    }
  }
  set fahrenheit(v) {
    this.quantity.value = v;
    this.quantity.unit = TemperatureUnit.fahrenheit;
  }
  get fahrenheit() {
    switch (this.quantity.unit) {
      case TemperatureUnit.celsius:
        return this.quantity.value * 1.8 + 32;
      case TemperatureUnit.kelvin:
        return this.quantity.value * 1.8 - 459.67;
      case TemperatureUnit.fahrenheit:
        return this.quantity.value;
      default:
        throw new Error('Temperature.fahrenheit: Unit not supported');
    }
  }
  set kelvin(v) {
    this.quantity.value = v;
    this.quantity.unit = TemperatureUnit.kelvin;
  }
  get kelvin() {
    switch (this.quantity.unit) {
      case TemperatureUnit.celsius:
        return this.quantity.value + 273.15;
      case TemperatureUnit.fahrenheit:
        return (this.quantity.value + 459.67) / 1.8;
      case TemperatureUnit.kelvin:
        return this.quantity.value;
      default:
        throw new Error('Temperature.kelvin: Unit not supported');
    }
  }
}

var VelocityUnit;
(function (VelocityUnit) {
  VelocityUnit["kilometerPerHour"] = "km_h";
  VelocityUnit["meterPerSecond"] = "m_s";
})(VelocityUnit || (VelocityUnit = {}));
class VelocityAccessor extends QuantityAccessor {
  // factory methods
  static from(q) {
    return new VelocityAccessor(q);
  }
  static meterPerSecond(v) {
    return new VelocityAccessor({
      value: v,
      unit: VelocityUnit.meterPerSecond
    });
  }
  static kilometerPerHour(v) {
    return new VelocityAccessor({
      value: v,
      unit: VelocityUnit.kilometerPerHour
    });
  }
  set meterPerSecond(v) {
    this.quantity.value = +v;
    this.quantity.unit = VelocityUnit.meterPerSecond;
  }
  get meterPerSecond() {
    switch (this.quantity.unit) {
      case VelocityUnit.kilometerPerHour:
        return this.quantity.value / 3.6;
      default:
      case VelocityUnit.meterPerSecond:
        return this.quantity.value;
    }
  }
  set kilometerPerHour(v) {
    this.quantity.value = +v;
    this.quantity.unit = VelocityUnit.kilometerPerHour;
  }
  get kilometerPerHour() {
    switch (this.quantity.unit) {
      case VelocityUnit.kilometerPerHour:
        return this.quantity.value;
      default:
      case VelocityUnit.meterPerSecond:
        return this.quantity.value * 3.6;
    }
  }
}
const velocity = q => {
  return VelocityAccessor.from(q);
};

var WeightUnit;
(function (WeightUnit) {
  WeightUnit["kilogram"] = "kg";
  WeightUnit["gram"] = "g";
})(WeightUnit || (WeightUnit = {}));
class WeightAccessor extends QuantityAccessor {
  // factory methods
  static from(q) {
    return new WeightAccessor(q);
  }
  static kilogram(v) {
    return new WeightAccessor({
      value: v,
      unit: WeightUnit.kilogram
    });
  }
  static gram(v) {
    return new WeightAccessor({
      value: v,
      unit: WeightUnit.gram
    });
  }
  get kilogram() {
    return this.gram / 1000;
  }
  set kilogram(v) {
    this.quantity.value = v;
    this.quantity.unit = WeightUnit.kilogram;
  }
  set gram(v) {
    this.quantity.value = v;
    this.quantity.unit = WeightUnit.gram;
  }
  get gram() {
    switch (this.quantity.unit) {
      case WeightUnit.kilogram:
        return this.quantity.value * 1000;
      default:
      case WeightUnit.gram:
        return this.quantity.value;
    }
  }
}
const weight = q => {
  return WeightAccessor.from(q);
};

var WeightPerLengthUnit;
(function (WeightPerLengthUnit) {
  WeightPerLengthUnit["kilogramPerKilometer"] = "kg_km";
  WeightPerLengthUnit["kilogramPerMeter"] = "kg_m";
  WeightPerLengthUnit["gramPerMeter"] = "g_m";
})(WeightPerLengthUnit || (WeightPerLengthUnit = {}));
class WeightPerLengthAccessor extends QuantityAccessor {
  // factory methods
  static from(q) {
    return new WeightPerLengthAccessor(q);
  }
  static kilogramPerKilometer(v) {
    return new WeightPerLengthAccessor({
      value: v,
      unit: WeightPerLengthUnit.kilogramPerKilometer
    });
  }
  static kilogramPerMeter(v) {
    return new WeightPerLengthAccessor({
      value: v,
      unit: WeightPerLengthUnit.kilogramPerMeter
    });
  }
  static gramPerMeter(v) {
    return new WeightPerLengthAccessor({
      value: v,
      unit: WeightPerLengthUnit.gramPerMeter
    });
  }
  set kilogramPerKilometer(v) {
    this.quantity.value = v;
    this.quantity.unit = WeightPerLengthUnit.kilogramPerKilometer;
  }
  get kilogramPerKilometer() {
    return this.kilogramPerMeter * 1000;
  }
  set kilogramPerMeter(v) {
    this.quantity.value = v;
    this.quantity.unit = WeightPerLengthUnit.kilogramPerMeter;
  }
  get kilogramPerMeter() {
    switch (this.quantity.unit) {
      case WeightPerLengthUnit.kilogramPerKilometer:
      case WeightPerLengthUnit.gramPerMeter:
        return this.quantity.value / 1000;
      default:
      case WeightPerLengthUnit.kilogramPerMeter:
        return this.quantity.value;
    }
  }
  set gramPerMeter(v) {
    this.quantity.value = v;
    this.quantity.unit = WeightPerLengthUnit.gramPerMeter;
  }
  get gramPerMeter() {
    return this.kilogramPerMeter * 1000;
  }
}
const weightPerLength = q => {
  return WeightPerLengthAccessor.from(q);
};

const TRANSLATION_KEY_BASE = 'kopla.units.';
/*  -------------------------------------------------------------------------------------------
 *  Acceleration
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Converts the specified acceleration value to its metric value (m/s² vs. ft/s²).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param a                  The acceleration value (in the specified {@link unitSystem}) to be converted
 * @returns                  The acceleration value in metric system
 */
const toMetricAcceleration = (unitSystem, a) => {
  if (unitSystem === UnitSystem.metric) {
    return a;
  } else {
    return a ? a / FACTOR_ACCELERATION : a;
  }
};
/**
 * Returns the Acceleration in the required unit system (m/s² vs. ft/s²).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param a                  The accelleration value in metric system
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricAcceleration = (targetUnitSystem, a) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return a;
  } else {
    return a ? a * FACTOR_ACCELERATION : a;
  }
};
/**
 * Returns the localization key to get the localized symbol for acceleration (m/s² or ft/s²)
 */
const getLocalizationKeyForAccelerationSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'M/S^2';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'FT/S^2';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Angle
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Angle value as a metric value.
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param alpha              The angle value (in the specified {@link unitSystem}) to be converted
 */
const toMetricAngle = (unitSystem, alpha) => {
  if (unitSystem === UnitSystem.metric) {
    return alpha;
  } else {
    return alpha;
  }
};
/**
 * Returns the Angle in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 */
const fromMetricAngle = (targetUnitSystem, alpha) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return alpha;
  } else {
    return alpha;
  }
};
/**
 * Returns the localization key to get the localized symbol for angle (°)
 */
const getLocalizationKeyForAngleSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + '°';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + '°';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Angle per distance
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the localization key to get the localized symbol for angle per distance (°/m or °/ft)
 */
const getLocalizationKeyForAnglePerDistanceSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + '°/M';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + '°/FT';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Angularvelocity
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Angularvelocity value as an metric value.
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param omega              The input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricAngularvelocity = (unitSystem, omega) => {
  if (unitSystem === UnitSystem.metric) {
    return omega;
  } else {
    return omega;
  }
};
/**
 * Returns the Angularvelocity in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricAngularvelocity = (targetUnitSystem, omega) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return omega;
  } else {
    return omega;
  }
};
/**
 * Returns the localization key to get the localized symbol for angular velocity (°/s)
 */
const getLocalizationKeyForAngularVelocitySymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + '°/S';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + '°/S';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Angularaccelaration
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Angularacceleration value as an metric value.
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param alpha              The input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricAngularacceleration = (unitSystem, alpha) => {
  if (unitSystem === UnitSystem.metric) {
    return alpha;
  } else {
    return alpha;
  }
};
/**
 * Returns the Angularacceleration in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricAngularacceleration = (targetUnitSystem, alpha) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return alpha;
  } else {
    return alpha;
  }
};
/**
 * Returns the localization key to get the localized symbol for angular acceleration (°/s²)
 */
const getLocalizationKeyForAngularAccelerationSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + '°/S^2';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + '°/S^2';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Area
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Milli Area value as an metric value (mm² vs. in²).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param a                  The AreaMilli input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricAreaMilli = (unitSystem, a) => {
  if (unitSystem === UnitSystem.metric) {
    return a;
  } else {
    return a ? a / FACTOR_AREA_MILLI : a;
  }
};
/**
 * Returns the passed Area value as an metric value (m² vs. ft²).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param a                  The area input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricArea = (unitSystem, a) => {
  if (unitSystem === UnitSystem.metric) {
    return a;
  } else {
    return a ? a / FACTOR_AREA : a;
  }
};
/**
 * Returns the passed Kilo Area value as an metric value (km² vs. mi²).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param a                  The AreaKilo input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricAreaKilo = (unitSystem, a) => {
  if (unitSystem === UnitSystem.metric) {
    return a;
  } else {
    return a ? a / FACTOR_AREA_KILO : a;
  }
};
/**
 * Returns the Milli Area in the required unit system (mm² vs. in²).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param a                  AreaMilli
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricAreaMilli = (targetUnitSystem, a) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return a;
  } else {
    return a ? a * FACTOR_AREA_MILLI : a;
  }
};
/**
 * Returns the Area in the required unit system (m² vs. ft²).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricArea = (targetUnitSystem, a) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return a;
  } else {
    return a ? a * FACTOR_AREA : a;
  }
};
/**
 * Returns the Kilo Area in the required unit system (km² vs. mi²).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param a                  AreaKilo
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricAreaKilo = (targetUnitSystem, a) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return a;
  } else {
    return a ? a * FACTOR_AREA_KILO : a;
  }
};
/**
 * Returns the localization key to get the localized symbol for area (m² or ft²)
 */
const getLocalizationKeyForAreaSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'M^2';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'FT^2';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for area in "milli" (mm² or in²)
 */
const getLocalizationKeyForAreaMilliSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'MM^2';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'IN^2';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for area in "kilo" (km² or mi²)
 */
const getLocalizationKeyForAreaKiloSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'KM^2';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'MI^2';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Distance
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Mikro Distance value as an metric value (µm vs. mil).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param l                  The DistanceMikro input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricDistanceMikro = (unitSystem, l) => {
  if (unitSystem === UnitSystem.metric) {
    return l;
  } else {
    return l ? l / FACTOR_DISTANCE_MIKRO : l;
  }
};
/**
 * Returns the passed Milli Distance value as an metric value (mm vs. in).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param l                  The DistanceMilli input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricDistanceMilli = (unitSystem, l) => {
  if (unitSystem === UnitSystem.metric) {
    return l;
  } else {
    return l ? l / FACTOR_DISTANCE_MILLI : l;
  }
};
/**
 * Returns the passed Distance value as an metric value (m vs ft).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param l                  The distance input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricDistance = (unitSystem, l) => {
  if (unitSystem === UnitSystem.metric) {
    return l;
  } else {
    return l ? l / FACTOR_DISTANCE : l;
  }
};
/**
 * Returns the passed Kilo Distance value as an metric value (km vs. mi).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param l                  The DistanceKilo input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricDistanceKilo = (unitSystem, l) => {
  if (unitSystem === UnitSystem.metric) {
    return l;
  } else {
    return l ? l / FACTOR_DISTANCE_KILO : l;
  }
};
/**
 * Returns the Mikro Distance in the required unit system (µm vs. mil).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param l                  DistanceMikro
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricDistanceMikro = (targetUnitSystem, l) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return l;
  } else {
    return l ? l * FACTOR_DISTANCE_MIKRO : l;
  }
};
/**
 * Returns the Milli Distance in the required unit system (mm vs. in).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param l                  The DistanceMilli in metric system to be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricDistanceMilli = (targetUnitSystem, l) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return l;
  } else {
    return l ? l * FACTOR_DISTANCE_MILLI : l;
  }
};
/**
 * Returns the Distance in the required unit system (m vs. ft).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricDistance = (targetUnitSystem, l) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return l;
  } else {
    return l ? l * FACTOR_DISTANCE : l;
  }
};
/**
 * Returns the Kilo Distance in the required unit system (km vs. mi).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param l                  DistanceKilo
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricDistanceKilo = (targetUnitSystem, l) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return l;
  } else {
    return l ? l * FACTOR_DISTANCE_KILO : l;
  }
};
/**
 * Returns the localization key to get the localized symbol for distance in "micro" (µm or mil)
 */
const getLocalizationKeyForDistanceMicroSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'µM';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'MIL';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for distance in "milli" (mm or in)
 */
const getLocalizationKeyForDistanceMilliSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'MM';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'IN';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for distance (m or ft)
 */
const getLocalizationKeyForDistanceSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'M';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'FT';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for distance in "kilo" (km or mi)
 */
const getLocalizationKeyForDistanceKiloSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'KM';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'MI';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Force
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Force value as an metric value (N vs. lbf).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param f                  The force input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricForce = (unitSystem, f) => {
  if (unitSystem === UnitSystem.metric) {
    return f;
  } else {
    return f ? f / FACTOR_FORCE : f;
  }
};
/**
 * Returns the passed KiloForce value as an metric value (kN vs. kipf).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param f                  The ForceKilo input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricForceKilo = (unitSystem, f) => {
  if (unitSystem === UnitSystem.metric) {
    return f;
  } else {
    return f ? f / FACTOR_FORCE_KILO : f;
  }
};
/**
 * Returns the Force in the required unit system (N vs. lbf).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricForce = (targetUnitSystem, f) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return f;
  } else {
    return f ? f * FACTOR_FORCE : f;
  }
};
/**
 * Returns the Kilo Force in the required unit system (kN vs. kipf).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param f                  ForceKilo
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricForceKilo = (targetUnitSystem, f) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return f;
  } else {
    return f ? f * FACTOR_FORCE_KILO : f;
  }
};
/**
 * Returns the localization key to get the localized symbol for force (N or lbf)
 */
const getLocalizationKeyForForceSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'N';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'LBF';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for force in "kilo" (kN or kipf)
 */
const getLocalizationKeyForForceKiloSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'KN';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'KIPF';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Mass
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Mass value as an metric value (g vs. oz).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param m                  The mass input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricMass = (unitSystem, m) => {
  if (unitSystem === UnitSystem.metric) {
    return m;
  } else {
    return m ? m / FACTOR_MASS : m;
  }
};
/**
 * Returns the passed Kilo Mass value as an metric value (kg vs. lb).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param m                  The MassKilo input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricMassKilo = (unitSystem, m) => {
  if (unitSystem === UnitSystem.metric) {
    return m;
  } else {
    return m ? m / FACTOR_MASS_KILO : m;
  }
};
/**
 * Returns the Mass in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricMass = (targetUnitSystem, m) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return m;
  } else {
    return m ? m * FACTOR_MASS : m;
  }
};
/**
 * Returns the Kilo Mass in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param m                  MassKilo
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricMassKilo = (targetUnitSystem, m) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return m;
  } else {
    return m ? m * FACTOR_MASS_KILO : m;
  }
};
/**
 * Returns the localization key to get the localized symbol for mass (g or oz)
 */
const getLocalizationKeyForMassSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'G';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'OZ';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for mass in "kilo" (kg or lb)
 */
const getLocalizationKeyForMassKiloSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'KG';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'LB';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Mass per distance
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed MassKiloPerDistance value as an metric value (kg/m vs. lb/ft).
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param v                  The input value (in the specified {@link unitSystem}) to be converted
 * @returns                  The value in its metric representation
 */
const toMetricMassKiloPerDistance = (unitSystem, v) => {
  if (unitSystem === UnitSystem.metric) {
    return v;
  } else {
    return v ? v * (FACTOR_DISTANCE / FACTOR_MASS_KILO) : v;
  }
};
/**
 * Returns the MassKiloPerDistance in the required unit system (kg/m vs. lb/ft).
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param v                  The input value (in metric unit)
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricMassKiloPerDistance = (targetUnitSystem, v) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return v;
  } else {
    return v ? v * (FACTOR_MASS_KILO / FACTOR_DISTANCE) : v;
  }
};
/**
 * Returns the localization key to get the localized symbol for mass (in "kilo") per distance (kg/m or lb/ft)
 */
const getLocalizationKeyForMassKiloPerDistanceSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'KG/M';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'LB/FT';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for acceleration (kg/km or lb/mft)
 */
const getLocalizationKeyForMassKiloPerDistanceKiloSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'KG/KM';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'LB/MFT';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  SMOA (Second_moment_of_area / Flächenträgheitsmoment)
 *  https://en.wikipedia.org/wiki/Second_moment_of_area
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Milli Smoa value as an metric value.
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param i                  The SmoaMilli input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricSmoaMilli = (unitSystem, i) => {
  if (unitSystem === UnitSystem.metric) {
    return i;
  } else {
    return i ? i / FACTOR_SMOA_MILLI : i;
  }
};
/**
 * Returns the passed Smoa value as an metric value.
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param i                  The Smoa input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricSmoa = (unitSystem, i) => {
  if (unitSystem === UnitSystem.metric) {
    return i;
  } else {
    return i ? i / FACTOR_SMOA : i;
  }
};
/**
 * Returns the Milli Smoa in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param i                  SmoaMilli
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricSmoaMilli = (targetUnitSystem, i) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return i;
  } else {
    return i ? i * FACTOR_SMOA_MILLI : i;
  }
};
/**
 * Returns the Smoa in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricSmoa = (targetUnitSystem, i) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return i;
  } else {
    return i ? i * FACTOR_SMOA : i;
  }
};
/**
 * Returns the localization key to get the localized symbol for Smoa in "milli" (mm⁴ or in⁴)
 */
const getLocalizationKeyForSmoaMilliSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'MM^4';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'IN^4';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for Smoa (m⁴ or ft⁴)
 */
const getLocalizationKeyForSmoaSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'M^4';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'FT^4';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Pressure
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Pressure value as an metric value (Pa vs. psi).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param p                  The pressure input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricPressure = (unitSystem, p) => {
  if (unitSystem === UnitSystem.metric) {
    return p;
  } else {
    return p ? p / FACTOR_PRESSURE : p;
  }
};
/**
 * Returns the passed Mega Pressure value as an metric value (MPa vs. psi).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param p                  The PressureMega input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricPressureMega = (unitSystem, p) => {
  if (unitSystem === UnitSystem.metric) {
    return p;
  } else {
    return p ? p / FACTOR_PRESSURE_MEGA : p;
  }
};
/**
 * Returns the Mass in the required unit system (Pa vs. psi).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricPressure = (targetUnitSystem, p) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return p;
  } else {
    return p ? p * FACTOR_PRESSURE : p;
  }
};
/**
 * Returns the Mega Pressure in the required unit system (MPa vs. psi).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param p                  PressureMega
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricPressureMega = (targetUnitSystem, p) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return p;
  } else {
    return p ? p * FACTOR_PRESSURE_MEGA : p;
  }
};
/**
 * Returns the localization key to get the localized symbol for pressure (Pa or psi)
 */
const getLocalizationKeyForPressureSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'PA';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'PSI';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for pressure (MPa or psi)
 */
const getLocalizationKeyForPressureMegaSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'MPA';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'PSI';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Resistance
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Resistance value as an metric value.
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param r                  The resistance input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricResistance = (unitSystem, r) => {
  if (unitSystem === UnitSystem.metric) {
    return r;
  } else {
    return r;
  }
};
/**
 * Returns the Resistance in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricResistance = (targetUnitSystem, r) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return r;
  } else {
    return r;
  }
};
/**
 * Returns the localization key to get the localized symbol for resistance (Ω)
 */
const getLocalizationKeyForResistanceSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'Ω';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'Ω';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Rotationalvelocity
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Rotationalvelocity value as an metric value.
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param omega              The input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricRotationalvelocity = (unitSystem, omega) => {
  if (unitSystem === UnitSystem.metric) {
    return omega;
  } else {
    return omega;
  }
};
/**
 * Returns the Rotationalvelocity in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricRotationalvelocity = (targetUnitSystem, omega) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return omega;
  } else {
    return omega;
  }
};
/**
 * Returns the localization key to get the localized symbol for rotational velocity (rpm)
 */
const getLocalizationKeyForRotationalVelocitySymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'RPM';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'RPM';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Temperature
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Temperature value as an metric value (°C vs. °F).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param t                  The temperature input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricTemperature = (unitSystem, t) => {
  if (unitSystem === UnitSystem.metric) {
    return t;
  } else {
    // TC = 5/9 ( TF/°F - 32) * °C
    return FACTOR_TEMPERATURE * (t - CONST_TEMPERATURE);
  }
};
/**
 * Returns the Temperature value in the required unit system (°C vs. °F).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricTemperature = (targetUnitSystem, t) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return t;
  } else {
    return t / FACTOR_TEMPERATURE + CONST_TEMPERATURE;
  }
};
/**
 * Returns the localization key to get the localized symbol for temperature (°C or °F)
 */
const getLocalizationKeyForTemperatureSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + '°C';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + '°F';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Time
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Time value as an metric value.
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param t                  The time input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricTime = (unitSystem, t) => {
  if (unitSystem === UnitSystem.metric) {
    return t;
  } else {
    return t;
  }
};
/**
 * Returns the Time in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricTime = (targetUnitSystem, t) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return t;
  } else {
    return t;
  }
};
/**
 * Returns the localization key to get the localized symbol for time, e.g. seconds (s)
 */
const getLocalizationKeyForTimeSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'S';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'S';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for hour (h)
 */
const getLocalizationKeyForTimeHourSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'H';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'H';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for minute (min)
 */
const getLocalizationKeyForTimeMinuteSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'MIN';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'MIN';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Torque
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Torque value as an metric value.
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param m                  The torque input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricTorque = (unitSystem, m) => {
  if (unitSystem === UnitSystem.metric) {
    return m;
  } else {
    return m ? m / FACTOR_TORQUE : m;
  }
};
/**
 * Returns the Torque in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricTorque = (targetUnitSystem, m) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return m;
  } else {
    return m ? m * FACTOR_TORQUE : m;
  }
};
/**
 * Returns the localization key to get the localized symbol for torque (Nm or lbf*ft)
 */
const getLocalizationKeyForTorqueSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'NM';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'LBF*FT';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Velocity
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Velocity value as an metric value (m/s vs. ft/s).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param v                  The velocity input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricVelocity = (unitSystem, v) => {
  if (unitSystem === UnitSystem.metric) {
    return v;
  } else {
    return v ? v / FACTOR_VELOCITY : v;
  }
};
/**
 * Returns the passed VelocityMinute value as an metric value (m/min vs. ft/min).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param v                  The input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricVelocityMinute = (unitSystem, v) => {
  if (unitSystem === UnitSystem.metric) {
    return v;
  } else {
    return v ? v / FACTOR_VELOCITY_MINUTE : v;
  }
};
/**
 * Returns the Velocity in the required unit system (m/s vs. ft/s).
 * Use this method in you getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricVelocity = (targetUnitSystem, v) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return v;
  } else {
    return v ? v * FACTOR_VELOCITY : v;
  }
};
/**
 * Returns the Velocity per Minute in the required unit system (m/min vs. ft/min).
 * Use this method in you getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricVelocityMinute = (targetUnitSystem, v) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return v;
  } else {
    return v ? v * FACTOR_VELOCITY_MINUTE : v;
  }
};
/**
 * Returns the localization key to get the localized symbol for velocity (m/s or ft/s)
 */
const getLocalizationKeyForVelocitySymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'M/S';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'FT/S';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for velocity in distance/minutes (m/min or ft/min)
 */
const getLocalizationKeyForVelocityMinuteSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'M/MIN';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'FT/MIN';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Volume
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Milli Volume value as an metric value (mm³ vs. in³).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param v                  The VolumeMilli input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricVolumeMilli = (unitSystem, v) => {
  if (unitSystem === UnitSystem.metric) {
    return v;
  } else {
    return v ? v / FACTOR_VOLUME_MILLI : v;
  }
};
/**
 * Returns the passed Volume value as an metric value (m³ vs. ft³).
 * Use this method in your setter to store the value you get from the
 * application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param v                  The volume input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricVolume = (unitSystem, v) => {
  if (unitSystem === UnitSystem.metric) {
    return v;
  } else {
    return v ? v / FACTOR_VOLUME : v;
  }
};
/**
 * Returns the Milli Volume in the required unit system (mm³ vs. in³).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param v                  VolumeMilli
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricVolumeMilli = (targetUnitSystem, v) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return v;
  } else {
    return v ? v * FACTOR_VOLUME_MILLI : v;
  }
};
/**
 * Returns the Volume in the required unit system (m³ vs. ft³).
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param v                  Volume
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricVolume = (targetUnitSystem, v) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return v;
  } else {
    return v ? v * FACTOR_VOLUME : v;
  }
};
/**
 * Returns the localization key to get the localized symbol for volumne in "milli" (mm³ or in³)
 */
const getLocalizationKeyForVolumeMilliSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'MM^3';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'IN^3';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/**
 * Returns the localization key to get the localized symbol for volumne (m³ or ft³)
 */
const getLocalizationKeyForVolumeSymbol = unitSystem => {
  switch (unitSystem) {
    case UnitSystem.metric:
      return TRANSLATION_KEY_BASE + 'M^3';
    case UnitSystem.imperial:
      return TRANSLATION_KEY_BASE + 'FT^3';
    default:
      throw new Error(`Undefined unit system: ${unitSystem}`);
  }
};
/*  -------------------------------------------------------------------------------------------
 *  Point
 *  -------------------------------------------------------------------------------------------
 */
/**
 * Returns the passed Milli point_in_distance value as an metric value.
 * Use this method in your setter to store the value you get from the application.
 *
 * @param unitSystem         The {@link UnitSystem} of the input value
 * @param p                  The point input value (in the specified {@link unitSystem}) to be converted
 */
const toMetricPointDistanceMilli = (unitSystem, p) => {
  if (unitSystem === UnitSystem.metric) {
    return p;
  } else {
    return {
      x: p.x / FACTOR_DISTANCE_MILLI,
      y: p.y / FACTOR_DISTANCE_MILLI,
      z: p.z / FACTOR_DISTANCE_MILLI
    };
  }
};
/**
 * Returns the Milli point_in_distance in the required unit system.
 * Use this method in your getter to return the value to the application.
 *
 * @param targetUnitSystem   The {@link UnitSystem} to which the value should be converted
 * @param p                  Point
 * @returns                  The value converted to the specified {@link targetUnitSystem}
 */
const fromMetricPointDistanceMilli = (targetUnitSystem, p) => {
  if (targetUnitSystem === UnitSystem.metric) {
    return p;
  } else {
    return {
      x: p.x * FACTOR_DISTANCE_MILLI,
      y: p.y * FACTOR_DISTANCE_MILLI,
      z: p.z * FACTOR_DISTANCE_MILLI
    };
  }
};

/**
 * Wraps deep-equal to ensure that all code uses the same solid deep-equal method
 * and that we can replace it later easily.
 *
 * It forces to use === comparison instead of ==
 */
const deepEqual = (a, b) => {
  return equal(a, b, {
    strict: true
  });
};

const clonePrimitive = obj => {
  // echo null/undefined
  if (obj == null) return obj;
  // echo literals
  if (Object(obj) !== obj) return obj;
  // clone
  try {
    return structuredClone(obj);
  } catch (_unused) {
    throw new Error('clonePrimitives: Error during structuredClone');
  }
};

/**
 * Checks if provided number value is NaN
 */
const isNaN = value => {
  return value !== value;
};

/**
 * Transforms a string into a number (if needed).
 */
const stringToNumber = value => {
  if (typeof value === 'string' && !Number.isNaN(Number(value) - parseFloat(value))) {
    return Number(value);
  }
  if (typeof value !== 'number') {
    throw new Error(`${value} is not a number`);
  }
  return value;
};

const safeStringify = value => {
  const seen = new WeakSet();
  const isHttpHeaders = node => {
    return node instanceof Object && 'keys' in node && 'getAll' in node;
  };
  const stringify = node => {
    var _node;
    if (typeof ((_node = node) == null ? void 0 : _node.toJSON) === 'function') {
      node = node.toJSON();
    }
    if (node instanceof Error) {
      node = {
        message: node.message || node.toString(),
        stack: node.stack
      };
    } else if (isHttpHeaders(node)) {
      const newNode = {};
      for (const key of node.keys()) {
        newNode[key] = node.getAll(key);
      }
      node = newNode;
    }
    if (node instanceof Map) {
      return stringify([...node]);
    }
    if (node === undefined || node === null || typeof node !== 'object') {
      return JSON.stringify(node);
    }
    if (Array.isArray(node)) {
      return `[${node.map(entry => {
        var _stringify;
        return (_stringify = stringify(entry)) != null ? _stringify : JSON.stringify(null);
      }).join(',')}]`;
    }
    if (seen.has(node)) {
      return JSON.stringify(`[Circular]`);
    }
    seen.add(node);
    const out = Object.keys(node).reduce((result, key) => {
      const child = stringify(node[key]);
      if (child) {
        result.push(`${JSON.stringify(key)}:${child}`);
      }
      return result;
    }, []);
    seen.delete(node);
    return `{${out.join(',')}}`;
  };
  return stringify(value);
};

export { AccelerationAccessor, AccelerationUnit, AngleAccessor, AnglePerLengthAccessor, AnglePerLengthUnit, AngleUnit, AngularAccelerationAccessor, AngularAccelerationUnit, AvailableLocalizedPdfServiceFonts, CONST_TEMPERATURE, ConfigurationFileCreator, ConsoleParser, DataConsistency, DefaultDataMigration, DefaultLogger, DeliveryTimeLevel, Domain, ExplanationJournal, FACTOR_ACCELERATION, FACTOR_AREA, FACTOR_AREA_KILO, FACTOR_AREA_MILLI, FACTOR_DISTANCE, FACTOR_DISTANCE_KILO, FACTOR_DISTANCE_MIKRO, FACTOR_DISTANCE_MILLI, FACTOR_FORCE, FACTOR_FORCE_KILO, FACTOR_MASS, FACTOR_MASS_KILO, FACTOR_PRESSURE, FACTOR_PRESSURE_MEGA, FACTOR_SMOA, FACTOR_SMOA_MILLI, FACTOR_TEMPERATURE, FACTOR_TORQUE, FACTOR_VELOCITY, FACTOR_VELOCITY_MINUTE, FACTOR_VOLUME, FACTOR_VOLUME_MILLI, ForceAccessor, ForceUnit, FrequencyAccessor, FrequencyUnit, GRAVITATIONAL_ACCELERATION, IproSvcAppServiceAppId, IproSvcAppServiceRequestMode, LengthAccessor, LengthUnit, LogfileParser, MAIL_CONSTANTS, MarketingCloudResponseErrorType, QuantityAccessor, SALESFORCE_COUNTRY_CODES, SalesforceCountryCode, TemperatureAccessor, TemperatureUnit, U, UnitSystem, V, ValidationResult, VelocityAccessor, VelocityUnit, W, WeightAccessor, WeightPerLengthAccessor, WeightPerLengthUnit, WeightUnit, acceleration, angle, anglePerLength, angularAcceleration, calcStraight, ceil, ceil1, ceil2, ceil3, ceil4, ceilBy, checkRangeU, checkRangeV, checkRangeW, clonePrimitive, deepEqual, defaultIProCulture, featureFlagHeader, featureFlagHeaderSeparator, featureFlagHeaderValueSeparator, floor, floor1, floor2, floor3, floor4, floorBy, force, frequency, fromMetricAcceleration, fromMetricAngle, fromMetricAngularacceleration, fromMetricAngularvelocity, fromMetricArea, fromMetricAreaKilo, fromMetricAreaMilli, fromMetricDistance, fromMetricDistanceKilo, fromMetricDistanceMikro, fromMetricDistanceMilli, fromMetricForce, fromMetricForceKilo, fromMetricMass, fromMetricMassKilo, fromMetricMassKiloPerDistance, fromMetricPointDistanceMilli, fromMetricPressure, fromMetricPressureMega, fromMetricResistance, fromMetricRotationalvelocity, fromMetricSmoa, fromMetricSmoaMilli, fromMetricTemperature, fromMetricTime, fromMetricTorque, fromMetricVelocity, fromMetricVelocityMinute, fromMetricVolume, fromMetricVolumeMilli, getBiggerUPoint, getBiggerUPoints, getBiggerUValue, getBiggerVPoint, getBiggerVPoints, getBiggerVValue, getBiggerWPoint, getBiggerWPoints, getBiggerWValue, getLocalizationKeyForAccelerationSymbol, getLocalizationKeyForAnglePerDistanceSymbol, getLocalizationKeyForAngleSymbol, getLocalizationKeyForAngularAccelerationSymbol, getLocalizationKeyForAngularVelocitySymbol, getLocalizationKeyForAreaKiloSymbol, getLocalizationKeyForAreaMilliSymbol, getLocalizationKeyForAreaSymbol, getLocalizationKeyForDistanceKiloSymbol, getLocalizationKeyForDistanceMicroSymbol, getLocalizationKeyForDistanceMilliSymbol, getLocalizationKeyForDistanceSymbol, getLocalizationKeyForForceKiloSymbol, getLocalizationKeyForForceSymbol, getLocalizationKeyForMassKiloPerDistanceKiloSymbol, getLocalizationKeyForMassKiloPerDistanceSymbol, getLocalizationKeyForMassKiloSymbol, getLocalizationKeyForMassSymbol, getLocalizationKeyForPressureMegaSymbol, getLocalizationKeyForPressureSymbol, getLocalizationKeyForResistanceSymbol, getLocalizationKeyForRotationalVelocitySymbol, getLocalizationKeyForSalesforceCountryCode, getLocalizationKeyForSmoaMilliSymbol, getLocalizationKeyForSmoaSymbol, getLocalizationKeyForTemperatureSymbol, getLocalizationKeyForTimeHourSymbol, getLocalizationKeyForTimeMinuteSymbol, getLocalizationKeyForTimeSymbol, getLocalizationKeyForTorqueSymbol, getLocalizationKeyForVelocityMinuteSymbol, getLocalizationKeyForVelocitySymbol, getLocalizationKeyForVolumeMilliSymbol, getLocalizationKeyForVolumeSymbol, getLowerUPoint, getLowerUPoints, getLowerUValue, getLowerVPoint, getLowerVPoints, getLowerVValue, getLowerWPoint, getLowerWPoints, getLowerWValue, getNextBiggerUPoints, getNextBiggerVPoints, getNextBiggerWPoints, getNextLowerUPoints, getNextLowerVPoints, getNextLowerWPoints, getSalesforceCountries, guessAdditionalPoints, iProCultures, iProDomains, iproSvcRequestModeSendsDirectlyToCustomer, isMailToCustomerCareRequestModel, isNaN, lPad, length, linearInterpolation1D, linearInterpolation2DfixU, linearInterpolation2DfixV, linearInterpolation3DfixUV, linearInterpolation3DfixUW, linearInterpolation3DfixVW, linearInterpolationBySection1D, linearInterpolationBySection2D, linearInterpolationBySection3D, marketingCloudBaseFieldInvalidPattern, marketingCloudBaseFieldValidPattern, point1DEqual, point1DListToString, point1DToString, point2DEqual, point2DListToString, point2DToString, point3DEqual, point3DListToString, point3DToString, round, round1, round10, round2, round3, round4, round5, roundBy, safeStringify, salesModeSessionHeaders, stringEqualsNumber, stringToNumber, toMetricAcceleration, toMetricAngle, toMetricAngularacceleration, toMetricAngularvelocity, toMetricArea, toMetricAreaKilo, toMetricAreaMilli, toMetricDistance, toMetricDistanceKilo, toMetricDistanceMikro, toMetricDistanceMilli, toMetricForce, toMetricForceKilo, toMetricMass, toMetricMassKilo, toMetricMassKiloPerDistance, toMetricPointDistanceMilli, toMetricPressure, toMetricPressureMega, toMetricResistance, toMetricRotationalvelocity, toMetricSmoa, toMetricSmoaMilli, toMetricTemperature, toMetricTime, toMetricTorque, toMetricVelocity, toMetricVelocityMinute, toMetricVolume, toMetricVolumeMilli, trunc, trunc1, trunc10, trunc2, trunc3, trunc4, trunc5, velocity, weight, weightPerLength };
